
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  Users, Activity, ShieldCheck, Search, Unlock, 
  ChevronLeft, ChevronRight, User as UserIcon, UserCheck, 
  Calendar, Globe, Building, Send,
  X,
  Brain, BrainCircuit, FileText,
  Timer, Mic,
  Check,
  Bug,
  Terminal,
  Wallet, DollarSign, Cpu, RefreshCw, ShieldAlert,
  UserMinus, UserPlus, Building2, CheckCircle, Radar,
  Star, MessageSquare, Zap, Bell, AlertTriangle, AlertCircle,
  MapPin, Map as MapIcon, Eye, ExternalLink, Save, Lock, FlaskConical, Pencil, Trash2
} from 'lucide-react';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, AreaChart, Area, Cell, PieChart as RePieChart, Pie
} from 'recharts';
import { auth, db, getDefaultDb } from '../firebaseConfig';
import { UserProfile, UserActivityStats, CollaborationStatus, SystemPricing, ROIEventType, ROIConfig, AppSuggestion, ModuleRating, LatencyEvent, LatencyModule, RegionalRestriction } from '../types';
import { 
  subscribeToAdminDirectory, fetchFullUserProfile, sendPeerMessage, fetchUserRealStats, 
  updateUserProfile, subscribeToSystemPricing, saveSystemPricing, 
  subscribeToSuggestions, subscribeToRatings, saveAppNotice, sendUserMessage,
  subscribeToLatency, subscribeToRegionalRestrictions, saveRegionalRestriction, deleteRegionalRestriction,
  getUserProfile, deleteUserProfileCompletely
} from '../services/db';
import { AppLogError, subscribeToLogs } from '../services/errorLogger';
import { fetchLatestGeminiPricing, analyzeSuggestionsWithAI, verifyProfessionalIdentity } from '../services/geminiService';
import { formatCurrency, formatHours, formatTokens, formatRating } from '../utils/format';
import { DEFAULT_ROI_TIMES, ROI_LABELS, PRICING_MAP } from '../constants';
import { COUNTRIES } from '../utils/regionalAccess';

const STATUS_COLORS: Record<string, string> = {
  'AVAILABLE': 'bg-emerald-500',
  'READING': 'bg-blue-500',
  'ON_CALL': 'bg-amber-500',
  'NOT_AVAILABLE': 'bg-red-500',
};

const STATUS_LABELS: Record<string, string> = {
  'AVAILABLE': 'DISPONIBLE',
  'READING': 'INFORMANDO',
  'ON_CALL': 'DE GUARDIA',
  'NOT_AVAILABLE': 'NO DISPONIBLE',
};

interface GlobalFinancialStats {
  totalCost: number;
  totalSearchOps: number;
  byModel: Record<string, { tokens: number; cost: number; label: string; color: string; history?: {date: string, tokens: number}[] }>;
  totalReports: number;
  totalPHD: number;
  totalHoursSaved: number; // Nuevo campo para ROI real basado en eventos
  hourlyROI: { label: string, hours: number, timestamp: number }[];
  dailyROI: { label: string, hours: number, timestamp: number }[];
  weeklyROI: { label: string, hours: number, timestamp: number }[];
  monthlyROI: { label: string, hours: number, timestamp: number }[];
  yearlyROI: { label: string, hours: number, timestamp: number }[];
}

// DEFAULT_ROI_TIMES moved to constants.ts

// Replica exacta de checkProfileIncomplete en App.tsx — mantener sincronizadas si esa lógica cambia.
const checkProfileIncomplete = (profile: any) => {
  if (!profile) return true;
  const essential = (profile.displayName || '').trim() !== '' &&
                    (profile.dni || '').trim() !== '' &&
                    (profile.college || '').trim() !== '' &&
                    (profile.collegeNumber || '').trim() !== '' &&
                    (profile.workplace || '').trim() !== '' &&
                    (profile.workCity || '').trim() !== '';

  if (profile.role === 'RESIDENT') return !essential;
  return !essential || !profile.mainSpecialty || !profile.specialties || profile.specialties.length < 1;
};

const ManagementScreen: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const theme = localStorage.getItem('app_theme');
    return theme === 'dark';
  });

  useEffect(() => {
    const handleThemeChange = () => {
      const theme = localStorage.getItem('app_theme');
      setIsDarkMode(theme === 'dark');
    };
    window.addEventListener('app_theme_changed', handleThemeChange);
    return () => window.removeEventListener('app_theme_changed', handleThemeChange);
  }, []);

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedUserStats, setSelectedUserStats] = useState<UserActivityStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [activeTab, setActiveTab] = useState<'DASHBOARD' | 'NETWORK' | 'USERS' | 'COMMUNICATIONS' | 'VALIDATION' | 'FEEDBACK' | 'LATENCY_CONTROL' | 'ERROR_LOGS' | 'DELEGATION' | 'REGIONAL_CONTROL' | 'BILLING'>('DASHBOARD');
  
  const TAB_LABELS: Record<string, string> = {
    'DASHBOARD': 'Panel de Control',
    'NETWORK': 'Red de Trabajo',
    'USERS': 'Usuarios',
    'COMMUNICATIONS': 'Comunicados',
    'VALIDATION': 'Validación Master',
    'FEEDBACK': 'Sugerencias',
    'LATENCY_CONTROL': 'Control Latencia',
    'ERROR_LOGS': 'Análisis',
    'DELEGATION': 'Delegación',
    'REGIONAL_CONTROL': 'Control Regional',
    'BILLING': 'Gastos'
  };

  const [billingBreakdown, setBillingBreakdown] = useState<Array<{ uid: string; displayName: string; email: string; tokenCost: number; baseFee: number; isCustomFee: boolean }>>([]);
  const [isLoadingBilling, setIsLoadingBilling] = useState(false);

  // Regional Restrictions States
  const [regionalRestrictions, setRegionalRestrictions] = useState<RegionalRestriction[]>([]);
  const [loadingRegionalRestrictions, setLoadingRegionalRestrictions] = useState(true);
  const [ruleForm, setRuleForm] = useState({
    type: 'COUNTRY' as 'COUNTRY' | 'CONTINENT',
    value: '',
    disabledModules: [] as string[],
    renameConclusionToSummary: false,
    disableDiferenciales: false
  });
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const ruleFormRef = useRef<HTMLDivElement>(null);
  const [regionalMessage, setRegionalMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [internalTestingSearch, setInternalTestingSearch] = useState('');
  const [updatingTestingBypassUid, setUpdatingTestingBypassUid] = useState<string | null>(null);
  const [showFullBreakdown, setShowFullBreakdown] = useState(false);
  const [focusedModel, setFocusedModel] = useState<string | null>(null);

  // Scroll asistido y adaptativo para las pestañas de navegación
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkTabsScrollable = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 6);
    setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 6);
  }, []);

  const handleScrollTabs = useCallback((direction: 'left' | 'right') => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const scrollAmount = 280;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;

    checkTabsScrollable();
    const resizeObserver = new ResizeObserver(() => {
      checkTabsScrollable();
    });
    resizeObserver.observe(el);
    window.addEventListener('resize', checkTabsScrollable);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkTabsScrollable);
    };
  }, [checkTabsScrollable]);

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const activeBtn = el.querySelector<HTMLElement>(`[data-tab="${activeTab}"]`);
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    const timer = setTimeout(checkTabsScrollable, 300);
    return () => clearTimeout(timer);
  }, [activeTab, checkTabsScrollable]);
  
  // Gestión de Precios y Fechas
  const [systemPricing, setSystemPricing] = useState<SystemPricing | null>(null);
  const [isUpdatingPricing, setIsUpdatingPricing] = useState(false);
  
  // Modal de Detalle de Perfil Profesional
  const [profileViewUid, setProfileViewUid] = useState<string | null>(null);
  const viewingProfileUser = useMemo(() => users.find(u => u.uid === profileViewUid), [users, profileViewUid]);

  // Estados para edición de territorio de perfil por el Master
  const [editWorkplace, setEditWorkplace] = useState('');
  const [editWorkCity, setEditWorkCity] = useState('');
  const [editWorkCountry, setEditWorkCountry] = useState('España');
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const [locationSaveFeedback, setLocationSaveFeedback] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (viewingProfileUser) {
      setEditWorkplace(viewingProfileUser.workplace || '');
      setEditWorkCity(viewingProfileUser.workCity || '');
      setEditWorkCountry(viewingProfileUser.workCountry || viewingProfileUser.country || 'España');
      setLocationSaveFeedback(null);
    }
  }, [viewingProfileUser]);

  const handleSaveLocation = async () => {
    if (!viewingProfileUser) return;
    setIsSavingLocation(true);
    setLocationSaveFeedback(null);
    try {
      const workplaceVal = editWorkplace.trim();
      const workCityVal = editWorkCity.trim();
      const workCountryVal = editWorkCountry.trim();

      await updateUserProfile(viewingProfileUser.uid, {
        workplace: workplaceVal,
        workCity: workCityVal,
        workCountry: workCountryVal,
        country: workCountryVal
      });

      setUsers(prev => prev.map(u => u.uid === viewingProfileUser.uid ? {
        ...u,
        workplace: workplaceVal,
        workCity: workCityVal,
        workCountry: workCountryVal,
        country: workCountryVal
      } : u));

      setLocationSaveFeedback({ text: 'Ubicación y territorio guardados correctamente en Firestore.', type: 'success' });
      setTimeout(() => setLocationSaveFeedback(null), 3500);
    } catch (err: any) {
      console.error("Error saving user location:", err);
      setLocationSaveFeedback({ text: `Error al guardar ubicación: ${err.message || err}`, type: 'error' });
      setTimeout(() => setLocationSaveFeedback(null), 4000);
    } finally {
      setIsSavingLocation(false);
    }
  };

  // Fecha de inicio global (desde systemPricing) o local (individual)
  const [globalStartDate, setGlobalStartDate] = useState<string>("");
  const [userFilterDate, setUserFilterDate] = useState<string>("");
  const [dirStats, setDirStats] = useState<{
    default: { profiles: number },
    current: { profiles: number }
  } | null>(null);
  const [isMigrating, setIsMigrating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [roiGranularity, setRoiGranularity] = useState<'hourly' | 'daily' | 'monthly' | 'yearly'>('daily');

  const checkDirectorySync = async () => {
    if (!auth.currentUser) {
      alert("Se requiere autenticación administrativa para realizar este análisis estructural.");
      return;
    }
    
    // Check if user is the master admin (Dr. Francisco Reyes)
    const isMaster = auth.currentUser.email?.toLowerCase() === 'dr.francisco.reyes@gmail.com' || 
                     auth.currentUser.uid === 'guest_dev_offline';
    
    if (!isMaster) {
      console.warn("[Directory] User is not recognized as Master Admin in frontend:", auth.currentUser.email);
    }

    setIsChecking(true);
    setDirectoryError(null);
    try {
      const { collection: coll, getDocs: getD, query: qry, limit: lmt, collectionGroup: collGroup } = await import('firebase/firestore');
      
      const stats = {
        default: { profiles: 0 },
        current: { profiles: 0 }
      };

      // Check Current DB
      const currentProfiles = await getD(coll(db, "profiles"));
      stats.current = {
        profiles: currentProfiles.size
      };

      // Check Default DB (Shared Auth)
      const defaultDb = getDefaultDb();
      
      console.log("[Directory] Checking default database directory with user:", auth.currentUser?.email);
      
      // Intentar primero con la colección raíz 'profiles'
      let defProfiles;
      try {
        defProfiles = await getD(coll(defaultDb, "profiles"));
        stats.default.profiles = defProfiles.size;
        console.log("[Directory] Root 'profiles' found:", defProfiles.size);
      } catch (e: any) {
        console.warn("[Directory] Root 'profiles' access denied or failed:", e.message);
        if (e.message.includes("permission-denied")) {
           console.error("[Directory] PERMISSION DENIED on (default) database. Rules on the old database might be blocking you.");
        }
        defProfiles = { size: 0, docs: [] };
      }
      
      // Si está vacía o falló, intentar con collectionGroup 'profile' (donde están los perfiles individuales)
      if (stats.default.profiles === 0) {
        try {
          const groupSnap = await getD(qry(collGroup(defaultDb, "profile"), lmt(500)));
          stats.default.profiles = groupSnap.size;
        } catch (e) {
          console.error("[Directory] CollectionGroup 'profile' failed:", e);
          // Último recurso: intentar listar 'users'
          try {
            const usersSnap = await getD(qry(coll(defaultDb, "users"), lmt(100)));
            stats.default.profiles = usersSnap.size;
          } catch (e2) {
            console.error("[Directory] All directory check methods failed:", e2);
            throw new Error("No se pudo acceder al directorio anterior. Es posible que la base de datos antigua tenga reglas de seguridad restrictivas.");
          }
        }
      }

      setDirStats(stats);
      console.log("[Directory] Stats updated:", stats);
    } catch (error) {
      console.error("Directory check error:", error);
      setDirectoryError((error as Error).message);
      alert("El análisis del directorio profesional no ha podido completarse. Por favor, reintente.");
    } finally {
      setIsChecking(false);
    }
  };

  const migrateDirectory = async () => {
    if (!auth.currentUser) {
      alert("Es necesario iniciar sesión con credenciales de administración para sincronizar el directorio.");
      return;
    }
    
    if (!confirm("¿Estás seguro de que quieres sincronizar el directorio global? Esto copiará los perfiles de usuario de la base de datos (default) a la actual para que todos sean visibles.")) return;
    
    setIsMigrating(true);
    try {
      const { collection: coll, getDocs: getD, setDoc: sDoc, doc: dRef, query: qry, limit: lmt, collectionGroup: collGroup } = await import('firebase/firestore');
      const defaultDb = getDefaultDb();

      let migratedCount = 0;
      const processedUids = new Set<string>();

      // 1. Try migrating from top-level 'profiles'
      try {
        const defProfiles = await getD(coll(defaultDb, "profiles"));
        for (const pDoc of defProfiles.docs) {
          await sDoc(dRef(db, "profiles", pDoc.id), pDoc.data(), { merge: true });
          processedUids.add(pDoc.id);
          migratedCount++;
        }
      } catch (e) {
        console.warn("Root profiles migration skipped or failed:", e);
      }

      // 2. Try migrating from collectionGroup 'profile'
      try {
        const groupSnap = await getD(collGroup(defaultDb, "profile"));
        for (const pDoc of groupSnap.docs) {
          const data = pDoc.data();
          const uid = data.uid || pDoc.id;
          if (uid && !processedUids.has(uid)) {
            await sDoc(dRef(db, "profiles", uid), {
              ...data,
              uid: uid,
              updatedAt: Date.now()
            }, { merge: true });
            
            // Also sync to subcollection in current DB
            await sDoc(dRef(db, "users", uid, "profile", "main"), data, { merge: true });
            
            processedUids.add(uid);
            migratedCount++;
          }
        }
      } catch (e) {
        console.warn("CollectionGroup profile migration skipped or failed:", e);
      }

      // 3. Fallback: Migrate from users collection directly
      if (migratedCount === 0 || processedUids.size < 5) {
        try {
          const usersSnap = await getD(qry(coll(defaultDb, "users"), lmt(100)));
          for (const uDoc of usersSnap.docs) {
            const userId = uDoc.id;
            if (processedUids.has(userId)) continue;

            try {
              const profileSnap = await getD(coll(defaultDb, "users", userId, "profile"));
              const mainProfile = profileSnap.docs.find(d => d.id === "main") || profileSnap.docs[0];
              
              if (mainProfile) {
                const profileData = mainProfile.data();
                await sDoc(dRef(db, "profiles", userId), {
                  ...profileData,
                  uid: userId,
                  updatedAt: Date.now()
                }, { merge: true });
                
                await sDoc(dRef(db, "users", userId, "profile", "main"), profileData, { merge: true });
                migratedCount++;
                processedUids.add(userId);
              } else {
                // Basic profile from user data
                const userData = uDoc.data();
                const basicProfile = {
                  uid: userId,
                  email: userData.email || '',
                  displayName: userData.displayName || 'Usuario Recuperado',
                  role: userData.role || 'USER',
                  isVerified: true,
                  createdAt: Date.now()
                };
                await sDoc(dRef(db, "profiles", userId), basicProfile, { merge: true });
                migratedCount++;
                processedUids.add(userId);
              }
            } catch (e) {
              console.warn(`Could not sync profile for user ${userId}:`, e);
            }
          }
        } catch (e) {
          console.error("Users fallback migration failed:", e);
        }
      }

      alert(`Sincronización de red finalizada con éxito. Se han integrado ${migratedCount} perfiles profesionales al directorio.`);
      checkDirectorySync();
    } catch (error) {
      console.error("Migration error:", error);
      alert("La sincronización del directorio no ha podido completarse en este momento.");
    } finally {
      setIsMigrating(false);
    }
  };
  const [isAuditing, setIsAuditing] = useState<string | null>(null);
  const [latencyEvents, setLatencyEvents] = useState<LatencyEvent[]>([]);
  const [appLogs, setAppLogs] = useState<AppLogError[]>([]);
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    const u = auth.currentUser;
    if (u && u.uid !== 'guest_dev_offline') {
      getUserProfile(u.uid).then(p => {
        if (p) setCurrentUserProfile(p);
      }).catch(err => {
        console.warn("[ManagementScreen] Error checking admin profile role:", err);
      });
    }
  }, []);

  const isMasterUser = useMemo(() => {
    const user = auth.currentUser;
    if (!user) return false;
    const email = user.email?.toLowerCase() || '';
    const isMaster = email === 'dr.francisco.reyes@gmail.com' || 
                     user.uid === 'DEamQ0aS5ZgqHUtcJzxpxeo1VVF2' ||
                     user.uid === 'guest_dev_offline';
    if (isMaster) return true;

    if (currentUserProfile && (currentUserProfile.role === 'ADMIN' || currentUserProfile.role === 'RADIOLOGY MASTER')) {
      return true;
    }

    const myProfile = users.find(u => u.uid === user.uid);
    if (myProfile && (myProfile.role === 'ADMIN' || myProfile.role === 'RADIOLOGY MASTER')) {
      return true;
    }

    return false;
  }, [auth.currentUser, currentUserProfile, users]);

  useEffect(() => {
    if (activeTab === 'ERROR_LOGS' && isMasterUser) {
      const unsub = subscribeToLogs(setAppLogs);
      return () => unsub();
    }
  }, [activeTab, isMasterUser]);

  useEffect(() => {
    if (activeTab === 'LATENCY_CONTROL' && isMasterUser) {
      const unsub = subscribeToLatency(setLatencyEvents, 200);
      return () => unsub();
    }
  }, [activeTab, isMasterUser]);

  useEffect(() => {
    if (activeTab !== 'BILLING' || !isMasterUser || users.length === 0) return;
    const loadBilling = async () => {
      setIsLoadingBilling(true);
      try {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        const results = await Promise.allSettled(users.map(u => fetchUserRealStats(u.uid, startOfMonth)));
        const breakdown = users.map((u, i) => {
          const r = results[i];
          const tokenCost = r.status === 'fulfilled' ? r.value.tokens.totalCost : 0;
          const isCustomFee = u.customBaseFee !== undefined && u.customBaseFee !== null;
          const baseFee = isCustomFee ? (u.customBaseFee as number) : (systemPricing?.baseFeeDefault ?? 30);
          return {
            uid: u.uid,
            displayName: u.displayName || u.email || u.uid,
            email: u.email || '',
            tokenCost,
            baseFee,
            isCustomFee
          };
        });
        setBillingBreakdown(breakdown);
      } catch (e) {
        console.error("Error cargando desglose de gastos:", e);
      } finally {
        setIsLoadingBilling(false);
      }
    };
    loadBilling();
  }, [activeTab, isMasterUser, users, systemPricing]);

  const handleUpdateUserBaseFee = async (uid: string, newFee: number | null) => {
    await updateUserProfile(uid, { customBaseFee: newFee === null ? null : newFee } as any);
    setBillingBreakdown(prev => prev.map(b => b.uid === uid ? { ...b, baseFee: newFee ?? (systemPricing?.baseFeeDefault ?? 30), isCustomFee: newFee !== null } : b));
  };

  useEffect(() => {
    const unsub = subscribeToSystemPricing((pricing) => {
        setSystemPricing(pricing);
        if (pricing) {
            localStorage.setItem('radia_dynamic_pricing', JSON.stringify(pricing));
            if (pricing.billingStartDate) {
                const d = new Date(pricing.billingStartDate);
                setGlobalStartDate(d.toISOString().split('T')[0]);
            }
        }
    });
    return () => unsub();
  }, []);

  const handleUpdateGlobalStartDate = async (dateStr: string) => {
      if (!systemPricing) return;
      const timestamp = new Date(dateStr).getTime();
      setGlobalStartDate(dateStr);
      await saveSystemPricing({
          ...systemPricing,
          billingStartDate: timestamp
      });
  };

  const handleUpdatePrices = async () => {
     if (isUpdatingPricing) return;
     setIsUpdatingPricing(true);
     try {
        const newPricing = await fetchLatestGeminiPricing();
        await saveSystemPricing({
            ...newPricing,
            billingStartDate: systemPricing?.billingStartDate || Date.now()
        });
        alert("Las tarifas de procesamiento oficial han sido actualizadas con éxito.");
    } catch {
        alert("No se ha podido establecer conexión para sincronizar las tarifas oficiales.");
     } finally {
        setIsUpdatingPricing(false);
     }
  };
  
  const [globalStats, setGlobalStats] = useState<GlobalFinancialStats>({
    totalCost: 0,
    totalSearchOps: 0,
    byModel: {},
    totalReports: 0,
    totalPHD: 0,
    totalHoursSaved: 0,
    hourlyROI: [],
    dailyROI: [],
    weeklyROI: [],
    monthlyROI: [],
    yearlyROI: []
  });

  const [suggestions, setSuggestions] = useState<AppSuggestion[]>([]);
  const [ratings, setRatings] = useState<ModuleRating[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    const unsubS = subscribeToSuggestions(setSuggestions);
    const unsubR = subscribeToRatings(setRatings);
    return () => { unsubS(); unsubR(); };
  }, []);

  useEffect(() => {
    setLoadingRegionalRestrictions(true);
    const unsub = subscribeToRegionalRestrictions((data) => {
      setRegionalRestrictions(data);
      setLoadingRegionalRestrictions(false);
    });
    return unsub;
  }, []);

  const handleSaveRegionalRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ruleForm.value.trim()) {
      setRegionalMessage({ text: 'Por favor, selecciona o escribe un nombre de país o continente válido.', type: 'error' });
      return;
    }
    try {
      await saveRegionalRestriction({
        ...(editingRuleId ? { id: editingRuleId } : {}),
        type: ruleForm.type,
        value: ruleForm.value.trim(),
        disabledModules: ruleForm.disabledModules,
        renameConclusionToSummary: ruleForm.renameConclusionToSummary,
        disableDiferenciales: ruleForm.disableDiferenciales
      });
      const wasEditing = !!editingRuleId;
      setEditingRuleId(null);
      setRuleForm({ 
        type: 'COUNTRY', 
        value: '', 
        disabledModules: [],
        renameConclusionToSummary: false,
        disableDiferenciales: false
      });
      setRegionalMessage({ 
        text: wasEditing 
          ? 'Regla de control regional actualizada correctamente en Firestore.' 
          : 'Regla de control regional guardada correctamente en Firestore.', 
        type: 'success' 
      });
      setTimeout(() => setRegionalMessage(null), 4000);
    } catch (e: any) {
      setRegionalMessage({ text: `Error al guardar regla: ${e.message || e}`, type: 'error' });
    }
  };

  const handleStartEditRegionalRule = (rule: RegionalRestriction) => {
    setEditingRuleId(rule.id);
    setRuleForm({
      type: rule.type || 'COUNTRY',
      value: rule.value || '',
      disabledModules: rule.disabledModules || [],
      renameConclusionToSummary: !!rule.renameConclusionToSummary,
      disableDiferenciales: !!rule.disableDiferenciales
    });
    setRegionalMessage(null);
    setTimeout(() => {
      if (ruleFormRef.current) {
        ruleFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  const handleCancelEditRegionalRule = () => {
    setEditingRuleId(null);
    setRuleForm({
      type: 'COUNTRY',
      value: '',
      disabledModules: [],
      renameConclusionToSummary: false,
      disableDiferenciales: false
    });
  };

  const handleDeleteRegionalRule = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta regla regional? Los usuarios de esta región recuperarán la visibilidad de los módulos inmediatamente.')) return;
    try {
      await deleteRegionalRestriction(id);
      if (editingRuleId === id) {
        handleCancelEditRegionalRule();
      }
      setRegionalMessage({ text: 'Regla regional eliminada con éxito.', type: 'success' });
      setTimeout(() => setRegionalMessage(null), 4000);
    } catch (e: any) {
      setRegionalMessage({ text: `Error al eliminar: ${e.message || e}`, type: 'error' });
    }
  };

  const handleToggleModuleInRule = (moduleId: string) => {
    setRuleForm(prev => {
      const isSelected = prev.disabledModules.includes(moduleId);
      const newModules = isSelected
        ? prev.disabledModules.filter(id => id !== moduleId)
        : [...prev.disabledModules, moduleId];
      return { ...prev, disabledModules: newModules };
    });
  };

  const VIEW_STATE_LABELS: Record<string, string> = {
    'REPORTS': 'Informes',
    'PHRASES': 'Frases',
    'RECYCLE_BIN': 'Papelera',
    'DASHBOARD_USER': 'Panel Usuario',
    'CREATE_REPORT': 'Crear Informe',
    'EDIT_REPORT': 'Editar Informe',
    'CREATE_PHRASE': 'Crear Frase',
    'EDIT_PHRASE': 'Editar Frase',
    'MANAGE_FILTERS': 'Filtros',
    'MODULAR_1': 'Modular 1',
    'DICTAPHONE': 'Dictáfono',
    'PREF': 'Preferencias',
    'CHAT_IA': 'Chat IA',
    'COMMUNITY': 'Comunidad',
    'PROFILE': 'Perfil',
    'AI_DRAFTING': 'Redacción IA',
    'ADMIN_PANEL': 'Panel Admin',
    'AGENT': 'Agente',
    'PHD_ASSISTANCE': 'Asistencia PHD'
  };

  const REGIONAL_MODULES_MAP: Record<string, string> = {
    'DASHBOARD_USER': 'Dashboard',
    'REPORTS': 'Biblioteca',
    'DICTAPHONE': 'Transcripción IA',
    'MODULAR_1': 'Modular 1',
    'PHD_ASSISTANCE': 'Asistencia PHD',
    'CHAT_IA': 'Búsqueda IA',
    'COMMUNITY': 'Comunidad',
    'AGENT': 'Agente'
  };

  const moduleAverages = useMemo(() => {
    const sums: Record<string, { total: number, count: number }> = {};
    ratings.forEach(r => {
      if (!sums[r.module]) sums[r.module] = { total: 0, count: 0 };
      sums[r.module].total += r.rating;
      sums[r.module].count++;
    });
    return Object.entries(sums).map(([module, data]) => ({
      module,
      avg: data.total / data.count,
      count: data.count
    }));
  }, [ratings]);

  const handleAnalyzeSuggestions = async () => {
    if (suggestions.length === 0 || isAnalyzing) return;
    setIsAnalyzing(true);
    try {
      const analysis = await analyzeSuggestionsWithAI(suggestions);
      setAiAnalysis(analysis);
    } catch (error) {
      console.error("AI Analysis error:", error);
      alert("El análisis de sugerencias mediante IA no ha podido completarse.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // --- DELEGATION STATES & HANDLERS (Option B Coexisting) ---
  interface SystemDelegation {
    delegatedEmails: string[];
    history?: {
      email: string;
      action: 'GRANTED' | 'REVOKED';
      timestamp: number;
      by: string;
    }[];
  }

  const [delegation, setDelegation] = useState<SystemDelegation | null>(null);
  const [delegationEmail, setDelegationEmail] = useState('');
  const [delegationLoading, setDelegationLoading] = useState(false);
  const [delegationMessage, setDelegationMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const fetchDelegation = async () => {
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const docRef = doc(db, 'system', 'delegation');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setDelegation(docSnap.data() as SystemDelegation);
      } else {
        setDelegation({ delegatedEmails: [], history: [] });
      }
    } catch (error) {
      console.error('Error fetching delegation config:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'DELEGATION') {
      fetchDelegation();
    }
  }, [activeTab]);

  const handleDelegate = async () => {
    if (!delegationEmail.trim() || delegationLoading) return;
    setDelegationLoading(true);
    setDelegationMessage(null);

    const emailToDelegate = delegationEmail.toLowerCase().trim();

    try {
      // 1. Encontrar el perfil del usuario en la lista de usuarios cargada
      const targetUser = users.find(u => u.email?.toLowerCase().trim() === emailToDelegate);
      
      if (!targetUser) {
        setDelegationMessage({
          type: 'error',
          text: 'El correo ingresado no pertenece a ningún usuario registrado en el sistema.'
        });
        setDelegationLoading(false);
        return;
      }

      if (!targetUser.isVerified) {
        setDelegationMessage({
          type: 'error',
          text: 'El usuario debe estar validado en "Validación Master" antes de ser co-administrador.'
        });
        setDelegationLoading(false);
        return;
      }

      // 2. Actualizar el rol del usuario a ADMIN en Firestore
      await updateUserProfile(targetUser.uid, { role: 'ADMIN' });

      // 3. Registrar en /system/delegation
      const { doc, getDoc, setDoc } = await import('firebase/firestore');
      const docRef = doc(db, 'system', 'delegation');
      const docSnap = await getDoc(docRef);
      
      let currentEmails: string[] = [];
      let currentHistory: any[] = [];

      if (docSnap.exists()) {
        const data = docSnap.data();
        currentEmails = data.delegatedEmails || [];
        currentHistory = data.history || [];
      }

      if (!currentEmails.includes(emailToDelegate)) {
        currentEmails.push(emailToDelegate);
      }

      currentHistory.push({
        email: emailToDelegate,
        action: 'GRANTED',
        timestamp: Date.now(),
        by: auth.currentUser?.email || 'dr.francisco.reyes@gmail.com'
      });

      await setDoc(docRef, {
        delegatedEmails: currentEmails,
        history: currentHistory
      }, { merge: true });

      setDelegation({
        delegatedEmails: currentEmails,
        history: currentHistory
      });

      setDelegationEmail('');
      setDelegationMessage({
        type: 'success',
        text: `Se ha concedido acceso de co-administración a ${emailToDelegate} correctamente.`
      });
    } catch (error: any) {
      console.error("Error delegating:", error);
      setDelegationMessage({
        type: 'error',
        text: `Error al procesar delegación: ${error.message || error}`
      });
    } finally {
      setDelegationLoading(false);
    }
  };

  const handleRevoke = async (email: string, uid: string) => {
    if (delegationLoading) return;
    
    // Preguntar confirmación antes de revocar
    const confirmRevoke = window.confirm(`¿Estás seguro de que deseas revocar los privilegios de administración para ${email}?`);
    if (!confirmRevoke) return;

    setDelegationLoading(true);
    setDelegationMessage(null);
    const emailToRevoke = email.toLowerCase().trim();

    try {
      // 1. Degradamos el rol del usuario a RADIOLOGIST en Firestore
      await updateUserProfile(uid, { role: 'RADIOLOGIST' });

      // 2. Actualizar el documento de /system/delegation
      const { doc, getDoc, setDoc } = await import('firebase/firestore');
      const docRef = doc(db, 'system', 'delegation');
      const docSnap = await getDoc(docRef);
      
      let currentEmails: string[] = [];
      let currentHistory: any[] = [];

      if (docSnap.exists()) {
        const data = docSnap.data();
        currentEmails = data.delegatedEmails || [];
        currentHistory = data.history || [];
      }

      currentEmails = currentEmails.filter(e => e !== emailToRevoke);
      currentHistory.push({
        email: emailToRevoke,
        action: 'REVOKED',
        timestamp: Date.now(),
        by: auth.currentUser?.email || 'dr.francisco.reyes@gmail.com'
      });

      await setDoc(docRef, {
        delegatedEmails: currentEmails,
        history: currentHistory
      }, { merge: true });

      setDelegation({
        delegatedEmails: currentEmails,
        history: currentHistory
      });

      setDelegationMessage({
        type: 'success',
        text: `Se han revocado los privilegios de administración de ${emailToRevoke}.`
      });
    } catch (error: any) {
      console.error("Error revoking:", error);
      setDelegationMessage({
        type: 'error',
        text: `Error al revocar privilegios: ${error.message || error}`
      });
    } finally {
      setDelegationLoading(false);
    }
  };

  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const uid = auth.currentUser?.uid || 'master';
    const unsub = subscribeToAdminDirectory((data, error) => {
      console.log("[ManagementScreen] Admin directory received:", data.length, "users found.");
      if (error) {
        setDirectoryError(error);
      } else {
        setDirectoryError(null);
      }
      setUsers(data);
    });
    return () => unsub();
  }, [retryCount]);

  // AGREGACIÓN GLOBAL CON FILTRADO POR FECHA
  useEffect(() => {
    if (users.length > 0 && activeTab === 'DASHBOARD') {
      const startMs = globalStartDate ? new Date(globalStartDate).getTime() : 0;

      const aggregate = async () => {
        const stats: GlobalFinancialStats = { 
          totalCost: 0, 
          totalSearchOps: 0, 
          byModel: {}, 
          totalReports: 0, 
          totalPHD: 0,
          totalHoursSaved: 0, 
          hourlyROI: [],
          dailyROI: [],
          weeklyROI: [],
          monthlyROI: [],
          yearlyROI: []
        };
        
        // Ejecutar en paralelo pero capturar errores individuales para que uno no rompa todo
        const allStatsResults = await Promise.allSettled(users.map(u => fetchUserRealStats(u.uid, startMs)));
        const allStats = allStatsResults
          .filter(r => r.status === 'fulfilled')
          .map(r => (r as PromiseFulfilledResult<UserActivityStats>).value);
        
        const tempHourlyROI: Record<string, { hours: number, timestamp: number }> = {};
        const tempDailyROI: Record<string, { hours: number, timestamp: number }> = {};
        const tempWeeklyROI: Record<string, { hours: number, timestamp: number }> = {};
        const tempMonthlyROI: Record<string, { hours: number, timestamp: number }> = {};
        const tempYearlyROI: Record<string, { hours: number, timestamp: number }> = {};

        allStats.forEach(userStat => {
          stats.totalCost += userStat.tokens.totalCost;
          stats.totalSearchOps += userStat.tokens.searchCount;
          stats.totalReports += userStat.reports;
          
          // Sumar eventos de Asistencia PHD
          const phdEvents = userStat.roiEvents.filter(e => e.type === ROIEventType.PHD_ASSISTANCE).length;
          stats.totalPHD += phdEvents;

          // Sumar ROI real de todos los usuarios
          const userTotalHours = userStat.roiEvents.reduce((acc, curr) => acc + (curr.hoursSaved || 0), 0);
          stats.totalHoursSaved += userTotalHours;

          // ROI Agregado
          userStat.hourlyROI.forEach(h => {
              if (!tempHourlyROI[h.label]) tempHourlyROI[h.label] = { hours: 0, timestamp: h.timestamp || 0 };
              tempHourlyROI[h.label].hours += h.hours;
          });
          userStat.dailyROI.forEach(d => {
              if (!tempDailyROI[d.label]) tempDailyROI[d.label] = { hours: 0, timestamp: d.timestamp || 0 };
              tempDailyROI[d.label].hours += d.hours;
          });
          userStat.weeklyROI.forEach(w => {
              if (!tempWeeklyROI[w.label]) tempWeeklyROI[w.label] = { hours: 0, timestamp: w.timestamp || 0 };
              tempWeeklyROI[w.label].hours += w.hours;
          });
          userStat.monthlyROI.forEach(m => {
              if (!tempMonthlyROI[m.label]) tempMonthlyROI[m.label] = { hours: 0, timestamp: m.timestamp || 0 };
              tempMonthlyROI[m.label].hours += m.hours;
          });
          userStat.yearlyROI.forEach(y => {
              if (!tempYearlyROI[y.label]) tempYearlyROI[y.label] = { hours: 0, timestamp: y.timestamp || 0 };
              tempYearlyROI[y.label].hours += y.hours;
          });

          Object.entries(userStat.tokens.byModel).forEach(([modelId, data]) => {
            const d = data as { input: number; output: number; cost: number };
            if (!stats.byModel[modelId]) {
              const meta = (systemPricing?.models[modelId]) || PRICING_MAP[modelId] || PRICING_MAP['default-flash'];
              stats.byModel[modelId] = { tokens: 0, cost: 0, label: meta.label, color: meta.color, history: [] };
            }
            stats.byModel[modelId].tokens += (d.input + d.output);
            stats.byModel[modelId].cost += d.cost;

            // Mergear historial global para gráficos
            if (userStat.modelHistory[modelId]) {
                userStat.modelHistory[modelId].forEach(h => {
                    const existing = stats.byModel[modelId].history!.find(x => x.date === h.date);
                    if (existing) existing.tokens += h.tokens;
                    else stats.byModel[modelId].history!.push(h);
                });
            }
          });
        });

        stats.hourlyROI = Object.entries(tempHourlyROI).map(([label, data]) => ({ label, hours: data.hours, timestamp: data.timestamp })).sort((a,b) => a.timestamp - b.timestamp);
        stats.dailyROI = Object.entries(tempDailyROI).map(([label, data]) => ({ label, hours: data.hours, timestamp: data.timestamp })).sort((a,b) => a.timestamp - b.timestamp);
        stats.weeklyROI = Object.entries(tempWeeklyROI).map(([label, data]) => ({ label, hours: data.hours, timestamp: data.timestamp })).sort((a,b) => a.timestamp - b.timestamp);
        stats.monthlyROI = Object.entries(tempMonthlyROI).map(([label, data]) => ({ label, hours: data.hours, timestamp: data.timestamp })).sort((a,b) => a.timestamp - b.timestamp);
        stats.yearlyROI = Object.entries(tempYearlyROI).map(([label, data]) => ({ label, hours: data.hours, timestamp: data.timestamp })).sort((a,b) => a.timestamp - b.timestamp);
        setGlobalStats(stats);
      };
      aggregate();
    }
  }, [users, activeTab, systemPricing, globalStartDate]);

  useEffect(() => {
    if (selectedUser) {
      setIsLoadingStats(true);
      const startMs = userFilterDate ? new Date(userFilterDate).getTime() : 0;
      fetchUserRealStats(selectedUser.uid, startMs).then(stats => {
        setSelectedUserStats(stats);
        setIsLoadingStats(false);
      });
    }
  }, [selectedUser, userFilterDate]);

  // MAPA
  const mapRef = useRef<any>(null);
  const mapContainerId = "master-network-map";
  const [mapStatusFilter, setMapStatusFilter] = useState<CollaborationStatus | 'ALL'>('ALL');
  const [mapOnlyVerified, setMapOnlyVerified] = useState(false);
  const [mapFocusUser, setMapFocusUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (activeTab === 'NETWORK' && mapRef.current && mapFocusUser && mapFocusUser.location) {
      setTimeout(() => {
        if (!mapRef.current) return;
        const L = (window as any).L;
        if (L) {
          const lat = Number(mapFocusUser.location!.lat);
          const lng = Number(mapFocusUser.location!.lng);
          if (!isNaN(lat) && !isNaN(lng)) {
            mapRef.current.setView([lat, lng], 14);
            L.popup()
              .setLatLng([lat, lng])
              .setContent(`
                <div style="font-family: sans-serif; padding: 4px; min-width: 140px; color: #1f2937;">
                  <b style="font-size: 13px; color: #1e1b4b; display: block; margin-bottom: 2px;">
                    ${mapFocusUser.displayName || 'Usuario'}
                  </b>
                  <div style="font-size: 10px; color: #4338ca; font-weight: 800; text-transform: uppercase; margin-bottom: 4px;">
                    ${mapFocusUser.isVerified ? 'VERIFICADO ✓' : 'PENDIENTE DE VALIDACIÓN ⚠'}
                  </div>
                  <div style="font-size: 10px; color: #4b5563;">
                    <b>Centro:</b> ${mapFocusUser.workplace || 'Desconocido'}<br/>
                    <b>Ciudad:</b> ${mapFocusUser.workCity || ''}
                  </div>
                </div>
              `)
              .openOn(mapRef.current);
          }
        }
      }, 350);
    }
  }, [activeTab, mapFocusUser]);

  useEffect(() => {
    if (activeTab === 'NETWORK' && !mapRef.current) {
        const L = (window as any).L; if (!L) return;
        setTimeout(() => {
            const map = L.map(mapContainerId).setView([20, 0], 2);
            const theme = localStorage.getItem('app_theme') === 'dark' ? 'dark_all' : 'light_all';
            L.tileLayer(`https://{s}.basemaps.cartocdn.com/${theme}/{z}/{x}/{y}{r}.png`, { attribution: '&copy; CARTO' }).addTo(map);
            
            const points: any[] = [];
            users.filter(u => (mapStatusFilter === 'ALL' || u.status === mapStatusFilter) && u.location && u.location.lat !== undefined && u.location.lng !== undefined && (!mapOnlyVerified || u.isVerified)).forEach(u => {
                const lat = Number(u.location!.lat);
                const lng = Number(u.location!.lng);
                if (isNaN(lat) || isNaN(lng)) return;

                points.push(L.latLng(lat, lng));

                const color = u.status === 'AVAILABLE' ? '#10b981' : u.status === 'READING' ? '#3b82f6' : u.status === 'ON_CALL' ? '#f59e0b' : '#94a3b8';
                const statusLabel = STATUS_LABELS[u.status] || u.status;
                
                const marker = L.circleMarker([lat, lng], { 
                    radius: u.isVerified ? 11 : 8, 
                    fillColor: color, 
                    color: u.isVerified ? "#fbbf24" : "#fff", 
                    weight: u.isVerified ? 3 : 2, 
                    opacity: 1, 
                    fillOpacity: 0.9 
                }).addTo(map);
                
                marker.bindPopup(`
                    <div style="font-family: sans-serif; padding: 5px; min-width: 150px;">
                        <div style="display: flex; align-items: center; gap: 5px; margin-bottom: 5px;">
                            <b style="font-size: 14px;">${u.displayName}</b>
                            ${u.isVerified ? '<span style="color: #10b981; font-weight: bold;">✓</span>' : ''}
                        </div>
                        <div style="font-size: 11px; color: #666; margin-bottom: 3px;">${u.workplace || ''}</div>
                        <div style="font-size: 10px; color: #999; margin-bottom: 8px;">${u.mainSpecialty || ''}</div>
                        <div style="display: flex; align-items: center; gap: 6px; padding-top: 5px; border-top: 1px solid #eee;">
                            <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${color};"></div>
                            <span style="font-size: 10px; font-weight: 900; color: ${color}; letter-spacing: 0.5px;">${statusLabel}</span>
                        </div>
                    </div>
                `);
            });

            if (points.length > 0) {
                const bounds = L.latLngBounds(points);
                map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
            }

            mapRef.current = map;
        }, 100);
    }
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [activeTab, users, mapStatusFilter, mapOnlyVerified]);

  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [selectedUids, setSelectedUids] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);
  const [commFilters, setCommFilters] = useState({ workplace: '', specialty: '', secondary: '', telerad: false });
  const [messageModal, setMessageModal] = useState<{ isOpen: boolean, user: UserProfile | null, text: string }>({
    isOpen: false,
    user: null,
    text: ''
  });

  const [deleteConfirmUser, setDeleteConfirmUser] = useState<{ uid: string; displayName: string } | null>(null);

  const latencyStats = useMemo(() => {
    if (latencyEvents.length === 0) return null;
    
    const stats: Record<string, { total: number, count: number, errors: number, avg: number, max: number, label: string }> = {};
    
    latencyEvents.forEach(e => {
      if (!stats[e.module]) {
        stats[e.module] = { 
          total: 0, 
          count: 0, 
          errors: 0, 
          avg: 0, 
          max: 0,
          label: e.module === LatencyModule.TRANSCRIPTION ? 'Transcripción' :
                 e.module === LatencyModule.REPORT_GEN ? 'Generación' :
                 e.module === LatencyModule.MODULAR_1_FUSION ? 'Fusión Modular' : 'Asistente'
        };
      }
      stats[e.module].total += e.durationMs;
      stats[e.module].count++;
      if (e.status !== 'SUCCESS') stats[e.module].errors++;
      if (e.durationMs > stats[e.module].max) stats[e.module].max = e.durationMs;
    });

    Object.keys(stats).forEach(k => {
      stats[k].avg = stats[k].total / stats[k].count;
    });

    return stats;
  }, [latencyEvents]);

  const latencyChartData = useMemo(() => {
    const last20 = [...latencyEvents].reverse().slice(-30);
    return last20.map(e => ({
      time: new Date(e.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      ms: e.durationMs,
      module: e.module,
      status: e.status
    }));
  }, [latencyEvents]);

  // Added memoized filter logic for users in the communications tab
  const commFilteredUsers = useMemo(() => {
    return users.filter(u => {
      const workplaceMatch = !commFilters.workplace || (u.workplace && u.workplace.toLowerCase().includes(commFilters.workplace.toLowerCase()));
      const specialtyMatch = !commFilters.specialty || u.mainSpecialty === commFilters.specialty;
      const teleradMatch = !commFilters.telerad || u.teleRadiologyInterest;
      return workplaceMatch && specialtyMatch && teleradMatch;
    });
  }, [users, commFilters]);

  // Added toggle selection handler for broadcast list
  const handleToggleSelection = (uid: string) => {
    setSelectedUids(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  // Added send handler for broadcast messages
  const handleSendBroadcast = async () => {
    if (!broadcastMessage.trim() || selectedUids.size === 0 || isSending) return;
    setIsSending(true);
    try {
      const uids = Array.from(selectedUids);
      const now = Date.now();
      await Promise.all(uids.map(async (targetUid) => {
        const chatId = ['master', targetUid].sort().join('_');
        await sendPeerMessage(chatId, {
          id: `broadcast_${now}_${Math.random().toString(36).substr(2, 5)}`,
          senderId: 'master',
          text: broadcastMessage,
          timestamp: now
        });
      }));
      alert(`El comunicado masivo ha sido enviado a ${selectedUids.size} profesionales correctamente.`);
      setBroadcastMessage('');
      setSelectedUids(new Set());
    } catch (error) {
      console.error("Broadcast error:", error);
      alert("No se ha podido procesar el envío del comunicado. Por favor, verifique los destinatarios seleccionados.");
    } finally {
      setIsSending(false);
    }
  };

  const handleManualValidation = async (uid: string, isValid: boolean) => {
      try {
          await updateUserProfile(uid, { isVerified: isValid });
      } catch (error) {
          console.error("Validation error:", error);
          alert("No se ha podido actualizar el estado de validación profesional.");
      }
  };

  const handleToggleBlock = async (uid: string, block: boolean) => {
      try {
          await updateUserProfile(uid, { isBlocked: block });
      } catch (error) {
          console.error("Block error:", error);
          alert("No se ha podido modificar el estado de restricción del usuario.");
      }
  };

  const handleToggleBypass = async (uid: string, bypass: boolean) => {
      try {
          await updateUserProfile(uid, { bypassProfileLock: bypass });
      } catch (error) {
          console.error("Bypass error:", error);
          alert("No se ha podido modificar el estado del bypass de perfil.");
      }
  };

  const handleDeleteUser = async (uid: string) => {
      try {
          await deleteUserProfileCompletely(uid);
      } catch (error) {
          console.error("Delete user error:", error);
          alert("No se ha podido eliminar el usuario. Puede ser un problema de permisos en Firestore (reglas de seguridad) — verifica con el desarrollador.");
      } finally {
          setDeleteConfirmUser(null);
      }
  };

  const handleToggleInternalTestingBypass = async (uid: string, currentVal: boolean) => {
      setUpdatingTestingBypassUid(uid);
      try {
          await updateUserProfile(uid, { internalTestingBypass: !currentVal });
      } catch (error) {
          console.error("Internal testing bypass error:", error);
          alert("No se ha podido modificar el estado de la excepción de pruebas internas.");
      } finally {
          setUpdatingTestingBypassUid(null);
      }
  };

  const handleAiAudit = async (user: UserProfile) => {
    if (isAuditing) return;
    setIsAuditing(user.uid);
    try {
      const result = await verifyProfessionalIdentity(user);
      const auditTimestamp = Date.now();
      
      // Persistencia permanente en Firestore
      await updateUserProfile(user.uid, {
        aiPreApproved: result.isValid,
        validationReport: result.reason,
        aiConfidenceScore: result.confidenceScore,
        lastAuditedAt: auditTimestamp
      });

      // Actualización reactiva instantánea del estado local y del modal
      setUsers(prev => prev.map(u => u.uid === user.uid ? {
        ...u,
        aiPreApproved: result.isValid,
        validationReport: result.reason,
        aiConfidenceScore: result.confidenceScore,
        lastAuditedAt: auditTimestamp
      } : u));

      alert(`Auditoría de identidad guardada con éxito para ${user.displayName}.\n\n🎯 Certeza: ${result.confidenceScore}% (${result.matchLevel})\nEstado IA: ${result.isValid ? 'PRE-APROBADO POR IA (≥90%)' : 'REVISIÓN REQUERIDA (<90%)'}\n\nLa información ha quedado guardada en la base de datos para futuras consultas.\nRecuerda: la validación oficial definitiva sigue requiriendo tu autorización manual.`);
    } catch (error) {
      console.error("Audit error:", error);
      alert("El proceso de auditoría mediante IA no ha podido completarse. Por favor, reintente.");
    } finally {
      setIsAuditing(null);
    }
  };

  const handleSendMessage = async () => {
    if (!messageModal.user || !messageModal.text.trim() || !auth.currentUser) return;
    try {
      await sendUserMessage(messageModal.user.uid, {
        senderId: auth.currentUser.uid,
        senderName: "Radiology Master",
        text: messageModal.text,
        type: 'VALIDATION'
      });
      alert("La notificación administrativa ha sido enviada al panel del usuario con éxito.");
      setMessageModal({ isOpen: false, user: null, text: '' });
    } catch (error) {
      console.error("Send message error:", error);
      alert("No se ha podido procesar el envío de la notificación en este momento.");
    }
  };

  const handleSyncDirectory = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    console.log("[handleSyncDirectory] Starting aggressive directory sync...");
    try {
      const { getDocs, collection, doc, getDoc, setDoc } = await import('firebase/firestore');
      const { db } = await import('../firebaseConfig');
      
      // 1. Intentar por la colección optimizada 'profiles'
      let foundProfiles: UserProfile[] = [];
      try {
        console.log("[handleSyncDirectory] Scanning 'profiles' collection...");
        const profilesSnap = await getDocs(collection(db, "profiles"));
        foundProfiles = profilesSnap.docs.map(d => d.data() as UserProfile).filter(p => p && p.uid);
        console.log("[handleSyncDirectory] Profiles found:", foundProfiles.length);
      } catch (e: any) {
        console.warn("[handleSyncDirectory] Profiles scan failed:", e.message);
      }

      // 2. RESCATE DE UIDs (Buscar en todas partes para reconstruir el directorio)
      const potentialUids = new Set<string>();
      
      // Escanear chats
      try {
        console.log("[handleSyncDirectory] Attempting rescue from 'peer_chats'...");
        const chatsSnap = await getDocs(collection(db, "peer_chats"));
        chatsSnap.docs.forEach(d => {
          const data = d.data();
          if (data.participants) data.participants.forEach((p: string) => potentialUids.add(p));
        });
      } catch(e) {
        console.warn("Rescue from peer_chats failed", e);
      }

      // Escanear grupos
      try {
        console.log("[handleSyncDirectory] Attempting rescue from 'workGroups'...");
        const groupsSnap = await getDocs(collection(db, "workGroups"));
        groupsSnap.docs.forEach(d => {
          const data = d.data();
          if (data.members) data.members.forEach((m: string) => potentialUids.add(m));
        });
      } catch(e) {
        console.warn("Rescue from workGroups failed", e);
      }

      console.log("[handleSyncDirectory] Potential UIDs discovered:", potentialUids.size);

      // 3. Recuperar perfiles reales para esos UIDs
      for (const uid of potentialUids) {
        if (foundProfiles.find(p => p.uid === uid)) continue;
        try {
          const pSnap = await getDoc(doc(db, "users", uid, "profile", "main"));
          if (pSnap.exists()) {
            foundProfiles.push(pSnap.data() as UserProfile);
          } else {
            // Si no tiene perfil, crear uno básico para que sea visible
            const basicProfile: UserProfile = {
              uid,
              displayName: `Usuario ${uid.substring(0, 5)}`,
              email: "pendiente@radia.ai",
              role: 'USER',
              status: 'offline',
              isVerified: false,
              updatedAt: Date.now()
            };
            foundProfiles.push(basicProfile);
          }
        } catch(e) {
          console.warn(`Rescue for UID ${uid} failed`, e);
        }
      }

      if (foundProfiles.length === 0) {
        alert("No se han localizado perfiles para sincronizar en la red actual.");
        setIsSyncing(false);
        return;
      }

      // 4. Materializar en la colección global 'profiles'
      console.log("[handleSyncDirectory] Materializing", foundProfiles.length, "profiles...");
      let successCount = 0;
      for (const profile of foundProfiles) {
        try {
          await setDoc(doc(db, "profiles", profile.uid), {
            ...profile,
            updatedAt: Date.now()
          }, { merge: true });
          successCount++;
        } catch (e) {
          console.error(`Error materializing ${profile.uid}:`, e);
        }
      }

      alert(`Proceso de sincronización finalizado: ${successCount} perfiles integrados.`);
    } catch (error: any) {
      console.error("[handleSyncDirectory] Critical error:", error);
      alert("Se ha producido un error durante el proceso de sincronización.");
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className={`flex flex-col h-full transition-colors font-['Outfit'] ${isDarkMode ? 'bg-[#1e2024]' : 'bg-[#f8fafc]'}`}>
      <div className={`px-6 py-4 border-b flex flex-col gap-4 z-40 transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Fila 1: Título e Indicador de Contexto */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-600 p-2.5 rounded-2xl shadow-xl shrink-0">
              <ShieldAlert size={22} className="text-white" />
            </div>
            <div>
              <h1 className={`font-black text-xl tracking-tighter uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Centro de Mando MODULAR
              </h1>
              <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">
                Gestión Master & ROI Analytics
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider border flex items-center gap-2 ${
              isDarkMode ? 'bg-gray-800/80 text-gray-300 border-gray-700' : 'bg-gray-50 text-gray-600 border-gray-200'
            }`}>
              <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
              <span>{TAB_LABELS[activeTab]}</span>
            </span>
          </div>
        </div>

        {/* Fila 2: Barra de Pestañas Dedicada con Scroll Asistido y Flechas Chevron */}
        <div className="relative w-full flex items-center">
          {/* Botón de navegación izquierda */}
          {canScrollLeft && (
            <button 
              type="button" 
              onClick={() => handleScrollTabs('left')}
              title="Desplazar menú hacia la izquierda"
              aria-label="Desplazar menú hacia la izquierda"
              className={`absolute left-0 z-20 p-2 rounded-xl shadow-lg border transition-all -ml-1 sm:-ml-2 ${
                isDarkMode 
                  ? 'bg-gray-800 text-indigo-400 border-gray-600 hover:bg-gray-700 shadow-black/60' 
                  : 'bg-white text-indigo-600 border-gray-200 hover:bg-gray-50 shadow-slate-300/60'
              }`}
            >
              <ChevronLeft size={16} />
            </button>
          )}

          {/* Difuminado sutil a la izquierda */}
          {canScrollLeft && (
            <div className={`pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 rounded-l-2xl bg-gradient-to-r ${
              isDarkMode ? 'from-[#2a2d34] to-transparent' : 'from-gray-100 to-transparent'
            }`} />
          )}

          {/* Contenedor desplazable de pestañas */}
          <div 
            ref={tabsContainerRef}
            onScroll={checkTabsScrollable}
            className={`w-full flex items-center overflow-x-auto rounded-2xl border transition-colors p-1.5 scroll-smooth whitespace-nowrap gap-1.5 scrollbar-none ${
              isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-gray-100 border-gray-200'
            }`}
          >
            {(['DASHBOARD', 'NETWORK', 'USERS', 'COMMUNICATIONS', 'FEEDBACK', 'VALIDATION', 'LATENCY_CONTROL', 'ERROR_LOGS', 'DELEGATION', 'REGIONAL_CONTROL', 'BILLING'] as const).map(tab => (
              <button 
                key={tab} 
                data-tab={tab}
                onClick={() => setActiveTab(tab)} 
                className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all whitespace-nowrap shrink-0 ${
                  activeTab === tab 
                    ? (isDarkMode ? 'bg-gray-700 text-indigo-400 shadow-lg' : 'bg-white text-indigo-600 shadow-md') 
                    : 'text-gray-400 hover:text-gray-500'
                }`}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>

          {/* Difuminado sutil a la derecha */}
          {canScrollRight && (
            <div className={`pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 rounded-r-2xl bg-gradient-to-l ${
              isDarkMode ? 'from-[#2a2d34] to-transparent' : 'from-gray-100 to-transparent'
            }`} />
          )}

          {/* Botón de navegación derecha */}
          {canScrollRight && (
            <button 
              type="button" 
              onClick={() => handleScrollTabs('right')}
              title="Desplazar menú hacia la derecha"
              aria-label="Desplazar menú hacia la derecha"
              className={`absolute right-0 z-20 p-2 rounded-xl shadow-lg border transition-all -mr-1 sm:-mr-2 ${
                isDarkMode 
                  ? 'bg-gray-800 text-indigo-400 border-gray-600 hover:bg-gray-700 shadow-black/60' 
                  : 'bg-white text-indigo-600 border-gray-200 hover:bg-gray-50 shadow-slate-300/60'
              }`}
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
        {/* Banner de diagnóstico y sincronización del directorio médico */}
        {directoryError && (
          <div 
            id="directory-diagnostic-banner"
            className={`mb-6 p-4 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs transition-all ${
              directoryError.includes('MODO RESTRINGIDO')
                ? (isDarkMode ? 'bg-amber-950/40 border-amber-700/60 text-amber-200' : 'bg-amber-50 border-amber-300 text-amber-900')
                : (isDarkMode ? 'bg-red-950/40 border-red-700/60 text-red-200' : 'bg-red-50 border-red-300 text-red-900')
            }`}
          >
            <div className="flex items-start sm:items-center gap-3">
              <div className={`p-2 rounded-xl shrink-0 ${
                directoryError.includes('MODO RESTRINGIDO') 
                  ? 'bg-amber-500/20 text-amber-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                <AlertTriangle size={18} />
              </div>
              <div>
                <p className="font-black uppercase tracking-wider text-[11px] flex items-center gap-2">
                  <span>{directoryError.includes('MODO RESTRINGIDO') ? 'Directorio en Modo Resiliente' : 'Aviso de Acceso al Directorio'}</span>
                  <span className="text-[10px] font-bold opacity-75">({users.length} médicos cargados)</span>
                </p>
                <p className="text-[10px] opacity-90 mt-0.5 max-w-3xl leading-relaxed">
                  {directoryError}
                </p>
              </div>
            </div>
            <button
              type="button"
              id="btn-retry-directory-sync"
              onClick={() => setRetryCount(prev => prev + 1)}
              className={`px-4 py-2 rounded-xl font-black uppercase text-[10px] flex items-center gap-1.5 shrink-0 transition-all ${
                isDarkMode 
                  ? 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-600' 
                  : 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-200 shadow-sm'
              }`}
            >
              <RefreshCw size={12} />
              Reintentar Sincronización
            </button>
          </div>
        )}

        {activeTab === 'DASHBOARD' && (
              <div className="space-y-8 animate-in fade-in">
                {/* SECCIÓN 1: RENDIMIENTO OPERATIVO Y ROI SEMANAL */}
            <div className="space-y-4">
               <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] flex items-center gap-2"><Activity size={14}/> Eficiencia Radiológica & Ahorro de Vida</h4>
               <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div className="lg:col-span-1 grid grid-cols-1 gap-4">
                     <StatCard title="ROI Global" value={`+${formatHours(globalStats.totalHoursSaved)}`} sub="Ahorro Acumulado Real" icon={Timer} color="text-emerald-500" bg="bg-emerald-500/10" isDarkMode={isDarkMode} />
                     <div className="grid grid-cols-2 gap-4">
                        <StatCard title="Informes" value={globalStats.totalReports} sub="MODULAR" icon={FileText} color="text-blue-500" bg="bg-blue-500/10" isDarkMode={isDarkMode} />
                        <StatCard title="Asistencia PHD" value={globalStats.totalPHD} sub="Procesos IA" icon={Brain} color="text-orange-500" bg="bg-orange-500/10" isDarkMode={isDarkMode} />
                     </div>
                  </div>
                  <div className={`lg:col-span-3 p-8 rounded-[40px] border shadow-sm transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100 shadow-slate-200/50'}`}>
                     <div className="flex justify-between items-center mb-8">
                        <div>
                           <h3 className="text-sm font-black uppercase tracking-widest text-blue-500 mb-1">Ahorro de Tiempo Global</h3>
                           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              {roiGranularity === 'hourly' ? 'Horas recuperadas por horas' : 
                               roiGranularity === 'daily' ? 'Horas recuperadas por día' : 
                               roiGranularity === 'monthly' ? 'Horas recuperadas por mes' : 'Horas recuperadas por año'}
                           </p>
                           {roiGranularity === 'daily' && (
                             <p className="text-lg font-black uppercase tracking-tighter text-slate-500 mt-1">
                               {new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                             </p>
                           )}
                        </div>
                        <div className={`flex items-center p-1 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                            {(['hourly', 'daily', 'monthly', 'yearly'] as const).map((g) => (
                                <button
                                    key={g}
                                    onClick={() => setRoiGranularity(g)}
                                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                        roiGranularity === g 
                                        ? (isDarkMode ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-blue-600 shadow-sm')
                                        : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {g === 'hourly' ? 'Hoy' : g === 'daily' ? 'Diario' : g === 'monthly' ? 'Meses' : 'Anual'}
                                </button>
                            ))}
                        </div>
                     </div>
                     <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={
                                roiGranularity === 'hourly' ? globalStats.hourlyROI :
                                roiGranularity === 'daily' ? globalStats.dailyROI :
                                roiGranularity === 'monthly' ? globalStats.monthlyROI :
                                globalStats.yearlyROI
                            }>
                            <defs>
                              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#374151' : '#f1f5f9'} />
                            <XAxis 
                              dataKey="label" 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                              dy={10}
                            />
                            <YAxis 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                            />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                border: 'none',
                                borderRadius: '16px',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="hours" 
                              stroke="#3b82f6" 
                              strokeWidth={4}
                              fillOpacity={1} 
                              fill="url(#colorValue)" 
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                     </div>
                  </div>
               </div>
            </div>

            {/* SECCIÓN DE CALIBRACIÓN DE ROI */}
            <div className="space-y-4">
               <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] flex items-center gap-2"><RefreshCw size={14}/> Panel de Calibración de ROI Maestro</h4>
               <div className={`p-8 rounded-[40px] border shadow-sm transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100 shadow-slate-200/50'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                     {(Object.entries(DEFAULT_ROI_TIMES) as [ROIEventType, number][]).map(([type, defaultTime]) => {
                        const currentTime = systemPricing?.roiConfig?.times[type] ?? defaultTime;
                        return (
                           <div key={type} className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                              <div className="flex justify-between items-center mb-3">
                                 <span className="text-[9px] font-black text-indigo-500 uppercase tracking-tight">{ROI_LABELS[type as ROIEventType] || type.replace('_', ' ')}</span>
                                 <div className="flex items-center gap-1">
                                    <input 
                                       type="number" 
                                       value={currentTime}
                                       onChange={async (e) => {
                                          const newVal = parseInt(e.target.value) || 0;
                                          const newConfig: ROIConfig = {
                                             times: {
                                                ...(systemPricing?.roiConfig?.times || DEFAULT_ROI_TIMES),
                                                [type]: newVal
                                             }
                                          };
                                          await saveSystemPricing({
                                             ...(systemPricing || { models: {}, searchFlatFee: 0.035, lastUpdated: Date.now() }),
                                             roiConfig: newConfig
                                          });
                                       }}
                                       className={`w-12 text-center text-xs font-black rounded-lg border outline-none ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-emerald-500' : 'bg-white border-gray-200 text-emerald-600'}`}
                                    />
                                    <span className="text-[9px] font-bold text-gray-400 uppercase">min</span>
                                 </div>
                              </div>
                           </div>
                        );
                     })}
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center">
                     <p className="text-[9px] font-bold text-gray-400 uppercase italic">Los cambios se aplican instantáneamente a toda la red y recalculan el ROI futuro.</p>
                  </div>
               </div>
            </div>

            {/* SECCIÓN DE CALIFICACIONES DE MÓDULOS */}
            <div className="space-y-4">
               <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] flex items-center gap-2"><CheckCircle size={14}/> Calificación General de la Aplicación</h4>
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {moduleAverages.map(m => (
                    <div key={m.module} className={`p-6 rounded-3xl border ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{VIEW_STATE_LABELS[m.module] || m.module}</span>
                        <div className="flex items-center gap-1 text-amber-500">
                          <span className="text-lg font-black">{formatRating(m.avg)}</span>
                          <span className="text-xs">★</span>
                        </div>
                      </div>
                      <p className="text-[9px] font-bold text-gray-400 uppercase">{m.count} Calificaciones</p>
                      <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
                        <div className="bg-amber-500 h-full" style={{ width: `${(m.avg / 5) * 100}%` }} />
                      </div>
                    </div>
                  ))}
               </div>
            </div>

            {/* SECCIÓN 2: ECOSISTEMA FINANCIERO */}
            <div className="space-y-4">
               <div className="flex items-center justify-between">
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] flex items-center gap-2"><DollarSign size={14}/> Ecosistema Financiero Multi-Modelo</h4>
               </div>
               
               <div className={`p-8 rounded-[40px] border shadow-sm transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100 shadow-slate-200/50'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                     {(() => {
                        const getModelVisuals = (modelId: string) => {
                          const id = modelId.toLowerCase();
                          if (id.includes('lite')) return { icon: Zap, color: 'text-cyan-500', barColor: 'bg-cyan-500' };
                          if (id.includes('pro')) return { icon: BrainCircuit, color: 'text-indigo-500', barColor: 'bg-indigo-500' };
                          if (id.includes('1.5-flash')) return { icon: Mic, color: 'text-amber-500', barColor: 'bg-amber-500' };
                          return { icon: Cpu, color: 'text-blue-500', barColor: 'bg-blue-500' };
                        };
                        const topModels = Object.entries(globalStats.byModel)
                          .sort((a: any, b: any) => (b[1].tokens || 0) - (a[1].tokens || 0))
                          .slice(0, 3);
                        if (topModels.length === 0) {
                          return (
                            <div className="md:col-span-3 flex items-center justify-center text-[10px] font-bold uppercase text-gray-400 py-8">
                              Aún no hay datos de consumo de modelos en este período
                            </div>
                          );
                        }
                        return topModels.map(([modelId, data]: any) => {
                          const visuals = getModelVisuals(modelId);
                          return (
                            <ModelMetricCard
                              key={modelId}
                              icon={visuals.icon}
                              label={data.label}
                              tokens={data.tokens}
                              color={visuals.color}
                              barColor={visuals.barColor}
                              isDarkMode={isDarkMode}
                              onClick={() => setFocusedModel(focusedModel === modelId ? null : modelId)}
                            />
                          );
                        });
                     })()}
                     <div className={`space-y-4 border-l pl-8 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <div className="flex items-center gap-2 text-emerald-500"><Wallet size={20}/><span className="text-xs font-black uppercase">Facturación Total</span></div>
                        <p className={`text-4xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(globalStats.totalCost, '$')}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Gasto Consolidado</p>
                        <button onClick={() => setShowFullBreakdown(!showFullBreakdown)} className="text-[10px] font-black text-emerald-500 uppercase tracking-widest border-b border-emerald-500/30 hover:border-emerald-500 transition-all">{showFullBreakdown ? 'Ocultar Desglose' : 'Ver Desglose de Gastos'}</button>
                     </div>
                  </div>

                  {focusedModel && (
                    <div className="mt-10 pt-10 border-t border-gray-100 dark:border-gray-800 animate-in slide-in-from-top-4 duration-500">
                        <div className="flex justify-between items-center mb-6">
                            <h5 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Histórico de Consumo: {globalStats.byModel[focusedModel]?.label}</h5>
                            <button onClick={() => setFocusedModel(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"><X size={16}/></button>
                        </div>
                        <div className="h-48 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={globalStats.byModel[focusedModel]?.history || []}>
                                    <defs>
                                        <linearGradient id="colorModel" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={globalStats.byModel[focusedModel]?.color.replace('bg-', '') === 'blue-500' ? '#3b82f6' : '#6366f1'} stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor={globalStats.byModel[focusedModel]?.color.replace('bg-', '') === 'blue-500' ? '#3b82f6' : '#6366f1'} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#374151' : '#f1f5f9'} />
                                    <XAxis 
                                        dataKey="date" 
                                        axisLine={false} 
                                        tickLine={false} 
                                        tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                                    />
                                    <YAxis 
                                        axisLine={false} 
                                        tickLine={false} 
                                        tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                                    />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                            border: 'none',
                                            borderRadius: '16px',
                                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="tokens" 
                                        stroke={globalStats.byModel[focusedModel]?.color.replace('bg-', '') === 'blue-500' ? '#3b82f6' : '#6366f1'} 
                                        strokeWidth={4}
                                        fillOpacity={1} 
                                        fill="url(#colorModel)" 
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                  )}

                  {showFullBreakdown && (
                    <div className={`mt-12 pt-8 border-t animate-in slide-in-from-top-4 duration-300 ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                        <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6">Detalle de Facturación por Modelo (Calculado)</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {Object.entries(globalStats.byModel).map(([modelId, data]) => {
                                const d = data as { tokens: number; cost: number; label: string; color: string };
                                return <ModelUsageItem key={modelId} name={d.label} tokens={formatTokens(d.tokens)} cost={formatCurrency(d.cost, '$')} color={d.color} isDarkMode={isDarkMode} />;
                            })}
                            {globalStats.totalSearchOps > 0 && <ModelUsageItem name="Google Search Grounding" tokens={`${globalStats.totalSearchOps} ops`} cost={formatCurrency(globalStats.totalSearchOps * (systemPricing?.searchFlatFee || 0.035), '$')} color="bg-emerald-500" isDarkMode={isDarkMode} />}
                        </div>
                    </div>
                  )}
               </div>

            </div>
          </div>
        )}
        
        {/* NETWORK TAB */}
        {activeTab === 'NETWORK' && (
            <div className="h-full flex flex-col space-y-4 animate-in zoom-in-95">
                <div className="flex flex-wrap gap-2">
                    <button onClick={() => setMapStatusFilter('ALL')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${mapStatusFilter === 'ALL' ? (isDarkMode ? 'bg-indigo-600 text-white shadow-lg' : 'bg-indigo-600 text-white shadow-lg') : (isDarkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-white text-gray-400 border border-gray-200')}`}>Todos</button>
                    {(Object.keys(STATUS_COLORS) as CollaborationStatus[]).map(status => (
                        <button key={status} onClick={() => setMapStatusFilter(status)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all flex items-center gap-2 ${mapStatusFilter === status ? 'bg-indigo-600 text-white shadow-lg' : (isDarkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-white text-gray-400 border border-gray-200')}`}>
                            <div className={`w-2 h-2 rounded-full ${STATUS_COLORS[status]}`}></div> {STATUS_LABELS[status] || status}
                        </button>
                    ))}
                    <div className="flex-1" />
                    <button onClick={() => setMapOnlyVerified(!mapOnlyVerified)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all flex items-center gap-2 ${mapOnlyVerified ? 'bg-emerald-600 text-white shadow-lg' : (isDarkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-white text-gray-400 border border-gray-200')}`}>
                        <ShieldCheck size={14} /> {mapOnlyVerified ? 'Solo Verificados' : 'Todos'}
                    </button>
                </div>
                <div className={`flex-1 relative rounded-[40px] overflow-hidden border shadow-inner ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                    <div id={mapContainerId} className="absolute inset-0 z-10" />
                </div>
            </div>
        )}

        {/* USERS TAB */}
        {activeTab === 'USERS' && (
          <div className="h-full flex flex-col lg:flex-row overflow-hidden">
             <div className="flex-1 overflow-y-auto custom-scrollbar pr-4">
                <div className="mb-6 flex gap-3">
                   <div className="relative flex-1">
                      <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="text" placeholder="Buscar por nombre, especialidad o centro..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full pl-12 pr-4 py-4 rounded-3xl border text-sm font-black uppercase tracking-tight shadow-sm outline-none transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700 text-white focus:ring-1 focus:ring-indigo-900' : 'bg-white border-gray-100 text-gray-900 focus:ring-1 focus:ring-indigo-50 shadow-slate-200/50'}`} />
                   </div>
                   <button 
                    onClick={handleSyncDirectory}
                    disabled={isSyncing}
                    className={`px-6 rounded-3xl flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${isSyncing ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95'} ${isDarkMode ? 'bg-indigo-900/40 text-indigo-300 border border-indigo-800' : 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20'}`}
                   >
                     <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
                     {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
                   </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   {users.filter(u => (u.displayName || u.email || u.uid).toLowerCase().includes(searchTerm.toLowerCase())).map(u => (
                      <button key={u.uid} onClick={() => setSelectedUser(u)} className={`p-5 rounded-[32px] border text-left transition-all flex items-start space-x-4 ${selectedUser?.uid === u.uid ? (isDarkMode ? 'bg-indigo-950/20 border-indigo-500/50 shadow-xl scale-[1.02]' : 'bg-indigo-50 border-indigo-200 shadow-md scale-[1.02]') : (isDarkMode ? 'bg-[#2a2d34] border-gray-700 hover:bg-gray-800/50 shadow-sm' : 'bg-white border-gray-50 hover:bg-gray-50 shadow-sm')}`}>
                         <div className="relative shrink-0"><div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-sm ${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>{u.photoURL ? <img src={u.photoURL} className="w-full h-full rounded-2xl object-cover" alt="" /> : (u.displayName || u.email || 'U').substring(0,2).toUpperCase()}</div><div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-4 ${isDarkMode ? 'border-[#2a2d34]' : 'border-white'} ${STATUS_COLORS[u.status]}`}></div></div>
                         <div className="min-w-0 flex-1">
                             <div className="flex items-center gap-1.5">
                                <p className={`text-[14px] font-black truncate uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{u.displayName || u.email || u.uid.substring(0,8)}</p>
                                {u.isVerified && <ShieldCheck size={14} className="text-emerald-500 shrink-0" />}
                                 {u.internalTestingBypass && (
                                    <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-300 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-700 shrink-0">
                                       PRUEBA INTERNA
                                    </span>
                                 )}
                             </div>
                             <p className="text-[10px] font-bold text-indigo-500 uppercase truncate mb-1">{u.mainSpecialty || 'Radiólogo General'}</p>
                             <p className="text-[9px] font-bold text-gray-400 uppercase truncate flex items-center gap-1"><Building2 size={10}/> {u.workplace || 'Sin Institución'}</p>
                         </div>
                         <ChevronRight size={18} className="text-gray-300 mt-2" />
                      </button>
                   ))}
                </div>
             </div>
             {selectedUser && (
               <div className={`w-full lg:w-[480px] border-l p-10 overflow-y-auto custom-scrollbar animate-in slide-in-from-right-10 duration-500 transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
                  <div className="flex justify-between items-start mb-10">
                      <div>
                          <h3 className={`text-3xl font-black mb-1 leading-tight tracking-tighter ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedUser.displayName}</h3>
                          <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{selectedUser.uid.substring(0,12)} [UUID RED]</p>
                           {selectedUser.internalTestingBypass && (
                               <span className="mt-1 inline-block px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-300 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-700">
                                   PRUEBA INTERNA (BYPASS REGIONAL)
                               </span>
                           )}
                      </div>
                      <button onClick={() => setSelectedUser(null)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><X size={28}/></button>
                  </div>

                  {/* FILTRO DE FECHA INDIVIDUAL */}
                  <div className={`mb-8 p-4 rounded-2xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-2">Auditar desde:</label>
                    <input 
                        type="date" 
                        value={userFilterDate}
                        onChange={(e) => setUserFilterDate(e.target.value)}
                        className={`w-full px-3 py-2 rounded-xl text-xs font-black uppercase outline-none transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                    />
                  </div>

                  {isLoadingStats ? (
                    <div className="flex flex-col items-center py-20 opacity-40"><RefreshCw size={48} className="animate-spin text-indigo-500 mb-4" /><p className="text-[10px] font-black uppercase tracking-widest">Auditoría en Curso...</p></div>
                  ) : selectedUserStats && (
                    <div className="space-y-8">
                       <div className={`p-8 rounded-[40px] border transition-colors ${isDarkMode ? 'bg-indigo-900/10 border-indigo-900/30' : 'bg-indigo-50 border-indigo-100 shadow-sm'}`}>
                          <div className="flex items-center space-x-3 mb-8">
                             <div className="bg-indigo-600 p-2.5 rounded-2xl text-white shadow-xl shadow-indigo-600/30"><Wallet size={24}/></div>
                             <h4 className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Facturación IA Individual</h4>
                          </div>
                          <div className="grid grid-cols-2 gap-8 mb-8">
                             <div><p className="text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Inversión Ciclo</p><p className="text-4xl font-black text-indigo-600">{formatCurrency(selectedUserStats.tokens.totalCost, '$')}</p></div>
                             <div className="text-right"><p className="text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Tokens Red</p><p className={`text-xl font-black ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{(selectedUserStats.tokens.totalInput + selectedUserStats.tokens.totalOutput).toLocaleString()}</p></div>
                          </div>
                          <div className={`space-y-4 pt-6 border-t ${isDarkMode ? 'border-indigo-800' : 'border-indigo-200'}`}>
                             <ComplexityStat label="Flash (Dictado / RAD-1)" count={selectedUserStats.transcriptions} color="text-amber-500" bg="bg-amber-500/10" isDarkMode={isDarkMode} />
                             <ComplexityStat label="Pro (Auditoría / PhD)" count={selectedUserStats.drafting} color="text-indigo-600" bg="bg-indigo-600/10" isDarkMode={isDarkMode} />
                             <ComplexityStat label="Asistencia PHD (IA)" count={selectedUserStats.phdAssistance} color="text-orange-500" bg="bg-orange-500/10" isDarkMode={isDarkMode} />
                             <ComplexityStat label="Google Grounding (Search)" count={selectedUserStats.tokens.searchCount} color="text-blue-500" bg="bg-blue-500/10" isDarkMode={isDarkMode} />
                          </div>
                       </div>

                       {/* GRÁFICO ROI INDIVIDUAL */}
                       <div className={`p-8 rounded-[40px] border transition-colors ${isDarkMode ? 'bg-emerald-900/10 border-emerald-900/30' : 'bg-emerald-50 border-emerald-100 shadow-sm'}`}>
                          <div className="flex items-center space-x-3 mb-6"><Timer size={24} className="text-emerald-600"/><h4 className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Retorno de Inversión</h4></div>
                          <div className="h-32 w-full mb-4">
                                                           <ResponsiveContainer width="100%" height="100%">
                                 <AreaChart data={
                                     roiGranularity === 'hourly' ? selectedUserStats.hourlyROI :
                                     roiGranularity === 'daily' ? selectedUserStats.dailyROI :
                                     roiGranularity === 'monthly' ? selectedUserStats.monthlyROI :
                                     selectedUserStats.yearlyROI
                                  }>
                                    <defs>
                                       <linearGradient id="colorIndividual" x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                       </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#374151' : '#f1f5f9'} />
                                    <XAxis dataKey="label" hide />
                                    <YAxis hide />
                                    <Tooltip 
                                       contentStyle={{ 
                                          backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                          border: 'none',
                                          borderRadius: '12px',
                                          fontSize: '10px',
                                          fontWeight: 'bold'
                                       }}
                                    />
                                    <Area 
                                       type="monotone" 
                                       dataKey="hours" 
                                       stroke="#10b981" 
                                       strokeWidth={3}
                                       fillOpacity={1} 
                                       fill="url(#colorIndividual)" 
                                    />
                                 </AreaChart>
                              </ResponsiveContainer>
                          </div>
                          <div className="flex items-end justify-between border-t pt-4 dark:border-emerald-800">
                             <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Horas Recuperadas Ciclo</span>
                                                           <span className="text-3xl font-black text-emerald-600">{formatHours((roiGranularity === 'hourly' ? selectedUserStats.hourlyROI : roiGranularity === 'daily' ? selectedUserStats.dailyROI : roiGranularity === 'monthly' ? selectedUserStats.monthlyROI : selectedUserStats.yearlyROI).reduce((acc, curr) => acc + curr.hours, 0), 1)}</span>
                          </div>
                       </div>
                    </div>
                  )}
               </div>
             )}
          </div>
        )}

        {/* FEEDBACK TAB */}
        {activeTab === 'FEEDBACK' && (
          <div className="h-full flex flex-col gap-8 animate-in fade-in duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Ratings Overview */}
              <div className={`lg:col-span-1 p-8 rounded-[40px] border shadow-sm transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
                <div className="flex items-center gap-3 mb-8">
                  <div className="bg-amber-500 p-2.5 rounded-2xl text-white shadow-lg shadow-amber-500/20">
                    <Star size={24} fill="currentColor" />
                  </div>
                  <h4 className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Calificaciones</h4>
                </div>
                <div className="space-y-6">
                  {moduleAverages.map((m) => (
                    <div key={m.module} className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{m.module}</span>
                        <div className="flex items-center gap-1 text-amber-500">
                          <span className="text-lg font-black">{formatRating(m.avg)}</span>
                          <span className="text-xs">★</span>
                        </div>
                      </div>
                      <p className="text-[9px] font-bold text-gray-400 uppercase">{m.count} Calificaciones</p>
                      <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
                        <div className="bg-amber-500 h-full" style={{ width: `${(m.avg / 5) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                  {moduleAverages.length === 0 && (
                    <p className="text-[10px] font-bold text-gray-400 uppercase text-center py-10">Sin calificaciones aún</p>
                  )}
                </div>
              </div>

              {/* Suggestions List */}
              <div className={`lg:col-span-2 p-8 rounded-[40px] border shadow-sm flex flex-col transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
                <div className="flex justify-between items-center mb-8">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-600 p-2.5 rounded-2xl text-white shadow-lg shadow-blue-600/20">
                      <MessageSquare size={24} />
                    </div>
                    <h4 className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Sugerencias de Usuarios</h4>
                  </div>
                  <button 
                    onClick={handleAnalyzeSuggestions}
                    disabled={isAnalyzing || suggestions.length === 0}
                    className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${isAnalyzing ? 'bg-gray-600 text-gray-400' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-600/20'}`}
                  >
                    {isAnalyzing ? <RefreshCw size={14} className="animate-spin" /> : <BrainCircuit size={14} />}
                    Analizar con IA
                  </button>
                </div>

                {aiAnalysis && (
                  <div className={`mb-8 p-6 rounded-[32px] border-2 border-indigo-500/30 animate-in slide-in-from-top-4 ${isDarkMode ? 'bg-indigo-950/20' : 'bg-indigo-50'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <h5 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                        <Zap size={14} fill="currentColor" /> Análisis Estratégico IA
                      </h5>
                      <button onClick={() => setAiAnalysis(null)} className="text-gray-400 hover:text-red-500"><X size={16}/></button>
                    </div>
                    <div className={`text-xs leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {aiAnalysis}
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-2">
                  {suggestions.map((s) => (
                    <div key={s.id} className={`p-6 rounded-[32px] border transition-all ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className={`text-[11px] font-black uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{s.userName}</p>
                          <p className="text-[9px] font-bold text-gray-400 uppercase">{new Date(s.timestamp).toLocaleString()}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest ${
                          s.status === 'pending' ? 'bg-amber-100 text-amber-600' :
                          s.status === 'reviewed' ? 'bg-blue-100 text-blue-600' :
                          'bg-emerald-100 text-emerald-600'
                        }`}>
                          {s.status}
                        </span>
                      </div>
                      <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{s.text}</p>
                    </div>
                  ))}
                  {suggestions.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-20 opacity-30">
                      <MessageSquare size={48} className="mb-4" />
                      <p className="text-[10px] font-black uppercase tracking-widest">No hay sugerencias registradas</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* App Notices Management */}
            <div className={`p-8 rounded-[40px] border shadow-sm transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-red-600 p-2.5 rounded-2xl text-white shadow-lg shadow-red-600/20">
                  <Bell size={24} />
                </div>
                <h4 className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Gestión de Avisos del Sistema</h4>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-4">
                  <input 
                    type="text" 
                    placeholder="Título del Aviso" 
                    id="notice-title"
                    className={`w-full p-4 rounded-2xl border text-xs font-black uppercase outline-none transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900'}`}
                  />
                  <textarea 
                    placeholder="Contenido del Aviso..." 
                    id="notice-content"
                    className={`w-full h-32 p-4 rounded-2xl border text-xs font-medium outline-none resize-none transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900'}`}
                  />
                  <select 
                    id="notice-type"
                    className={`w-full p-4 rounded-2xl border text-xs font-black uppercase outline-none transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900'}`}
                  >
                    <option value="info">Informativo</option>
                    <option value="urgent">Urgente</option>
                    <option value="maintenance">Mantenimiento</option>
                  </select>
                  <button 
                    onClick={async () => {
                      const title = (document.getElementById('notice-title') as HTMLInputElement).value;
                      const content = (document.getElementById('notice-content') as HTMLTextAreaElement).value;
                      const type = (document.getElementById('notice-type') as HTMLSelectElement).value as any;
                      if (!title || !content) return;
                      await saveAppNotice({
                        title,
                        content,
                        type,
                        timestamp: Date.now(),
                        active: true
                      });
                      (document.getElementById('notice-title') as HTMLInputElement).value = '';
                      (document.getElementById('notice-content') as HTMLTextAreaElement).value = '';
                      alert('Comunicado publicado en el panel de anuncios.');
                    }}
                    className="w-full py-4 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg shadow-red-600/20 transition-all active:scale-95"
                  >
                    Publicar Aviso
                  </button>
                </div>
                
                <div className="lg:col-span-2 space-y-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Avisos Activos</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Los avisos se cargan mediante suscripción en un componente real, aquí mostramos el concepto */}
                    <p className="text-[10px] font-bold text-gray-400 uppercase italic">Los avisos publicados aparecerán instantáneamente en los dashboards de los usuarios.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'COMMUNICATIONS' && (
           <div className="h-full flex flex-col lg:flex-row gap-6 animate-in slide-in-from-bottom-5">
              <div className={`w-full lg:w-96 p-8 rounded-[40px] border shadow-sm shrink-0 h-fit transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
                 <h5 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-6">Filtros de Segmentación</h5>
                 <div className="space-y-4">
                    <div className="space-y-1"><span className="text-[9px] font-black text-gray-400 uppercase ml-2">Lugar de Trabajo</span><input type="text" value={commFilters.workplace} onChange={e => setCommFilters({...commFilters, workplace: e.target.value})} placeholder="Hospital, Clínica..." className={`w-full p-3 rounded-xl border text-xs font-bold uppercase transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900 focus:bg-white'}`} /></div>
                    <div className="space-y-1"><span className="text-[9px] font-black text-gray-400 uppercase ml-2">Especialidad Principal</span><select value={commFilters.specialty} onChange={e => setCommFilters({...commFilters, specialty: e.target.value})} className={`w-full p-3 rounded-xl border text-xs font-bold uppercase transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900 focus:bg-white'}`}><option value="">Cualquiera</option>{Array.from(new Set(users.map(u => u.mainSpecialty).filter(Boolean))).map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                    <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer group transition-colors ${isDarkMode ? 'hover:bg-indigo-900/10 border-gray-700' : 'hover:bg-indigo-50 border-gray-100'}`}><input type="checkbox" checked={commFilters.telerad} onChange={e => setCommFilters({...commFilters, telerad: e.target.checked})} className="w-4 h-4 text-indigo-600 rounded" /><span className="text-[10px] font-black text-gray-500 uppercase tracking-tighter">Interés Teleradiología</span></label>
                 </div>
              </div>
              <div className="flex-1 flex flex-col gap-6">
                 <div className={`flex-1 p-8 rounded-[40px] border shadow-sm overflow-hidden flex flex-col transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
                    <div className="flex justify-between items-center mb-6">
                        <h5 className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Usuarios Seleccionados ({selectedUids.size})</h5>
                        <div className="flex gap-2"><button onClick={() => setSelectedUids(new Set(commFilteredUsers.map(u => u.uid)))} className="text-[9px] font-black text-indigo-500 uppercase hover:underline">Marcar Todos</button><button onClick={() => setSelectedUids(new Set())} className="text-[9px] font-black text-red-500 uppercase hover:underline">Limpiar</button></div>
                    </div>
                    <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2">
                        {commFilteredUsers.map(u => (
                            <button key={u.uid} onClick={() => handleToggleSelection(u.uid)} className={`w-full p-3 rounded-2xl border flex items-center gap-3 transition-all ${selectedUids.has(u.uid) ? (isDarkMode ? 'bg-blue-900/10 border-blue-800 shadow-md' : 'bg-blue-50 border-blue-200 shadow-sm') : 'bg-transparent border-transparent opacity-60'}`}><div className={`w-6 h-6 rounded-md flex items-center justify-center transition-all ${selectedUids.has(u.uid) ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-200 text-transparent'}`}><Check size={14}/></div><span className={`text-[11px] font-black uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{u.displayName}</span><span className="text-[9px] font-bold text-gray-400 ml-auto uppercase">{u.mainSpecialty}</span></button>
                        ))}
                    </div>
                 </div>
                 <div className={`p-8 rounded-[40px] border shadow-xl transition-colors ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-white border-gray-200'}`}>
                    <textarea value={broadcastMessage} onChange={e => setBroadcastMessage(e.target.value)} placeholder="Escribe un mensaje masivo para los seleccionados..." className={`w-full h-24 p-4 rounded-2xl border text-xs font-medium outline-none resize-none mb-4 transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900 focus:bg-white'}`} />
                    <button onClick={handleSendBroadcast} disabled={isSending || selectedUids.size === 0 || !broadcastMessage.trim()} className={`w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl transition-all flex items-center justify-center gap-2 ${isSending ? 'bg-gray-600 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95 shadow-blue-600/20'}`}>{isSending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} EMITIR COMUNICADO A {selectedUids.size} RADIÓLOGOS</button>
                 </div>
              </div>
           </div>
        )}

        {activeTab === 'VALIDATION' && (
           <div className="space-y-10 animate-in fade-in">
              <div className="flex items-center justify-between mb-2">
                 <div>
                    <h2 className={`text-2xl font-black tracking-tighter uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Validación de Identidad Profesional</h2>
                    <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Supervisión Master de Registros</p>
                 </div>
                 <div className="flex items-center gap-4">
                    <button 
                       onClick={handleSyncDirectory}
                       disabled={isSyncing}
                       className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-[10px] font-black uppercase transition-all ${isDarkMode ? 'bg-gray-800 text-gray-400 hover:text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                       <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
                       Sincronizar Directorio
                    </button>
                    
                    <button 
                       onClick={async () => {
                         const { db } = await import('../firebaseConfig');
                         const { getDoc, doc } = await import('firebase/firestore');
                         const currentUser = auth.currentUser;
                         
                         let adminStatus = "Desconocido";
                         try {
                           const profileSnap = await getDoc(doc(db, "users", currentUser?.uid || '', "profile", "main"));
                           const role = profileSnap.exists() ? profileSnap.data().role : "Sin perfil";
                           adminStatus = role === 'ADMIN' ? "SÍ (Role)" : `NO (${role})`;
                           if (currentUser?.email?.toLowerCase() === 'dr.francisco.reyes@gmail.com') {
                             adminStatus += " - SÍ (Email Master)";
                           }
                         } catch (e: any) {
                           adminStatus = "Error: " + e.message;
                         }

                         console.log("DEBUG - ManagementScreen Users State:", users);
                         alert(
                           `DIAGNÓSTICO MASTER:\n\n` +
                           `- Usuarios en memoria: ${users.length}\n` +
                           `- Pendientes: ${users.filter(u => !u.isVerified).length}\n` +
                           `- Validados: ${users.filter(u => u.isVerified).length}\n` +
                           `- Bloqueados: ${users.filter(u => u.isBlocked).length}\n\n` +
                           `SESIÓN:\n` +
                           `- UID: ${currentUser?.uid}\n` +
                           `- Email: ${currentUser?.email}\n` +
                           `- Admin Status: ${adminStatus}\n\n` +
                           `Revise la consola (F12) para detalles.`
                         );
                       }}
                       className={`p-2 rounded-2xl border transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-400'}`}
                       title="Diagnóstico de Datos"
                    >
                       <Bug size={14} />
                    </button>

                    <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                       <Users size={16} className="text-indigo-500" />
                       <span className="text-[10px] font-black text-indigo-600 uppercase">{users.filter(u => !u.isVerified && !u.isBlocked).length} Pendientes</span>
                    </div>
                 </div>
              </div>

              {/* SECCIÓN: PENDIENTES DE IDENTIFICACIÓN */}
              <div className="space-y-4">
                 <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-6 bg-amber-500 rounded-full"></div>
                    <h3 className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Pendientes de Identificación / Incompletos</h3>
                 </div>
                 <div className="grid grid-cols-1 gap-4">
                    {users.filter(u => !u.isVerified).length === 0 && (
                       <div className={`p-12 rounded-[32px] border border-dashed flex flex-col items-center justify-center text-center ${isDarkMode ? 'bg-gray-800/20 border-gray-700' : 'bg-gray-50/50 border-gray-200'}`}>
                          <Users size={48} className="text-gray-300 mb-4" />
                          <p className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No hay usuarios pendientes de identificación</p>
                          <p className="text-[10px] font-bold text-gray-400 mt-2 uppercase">Intente sincronizar el directorio si espera ver nuevos registros</p>
                       </div>
                    )}
                    {users.filter(u => !u.isVerified).map(u => (
                       <div key={u.uid} className={`p-6 rounded-[32px] border transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                             <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden relative">
                                   {u.photoURL ? <img src={u.photoURL} className="w-full h-full object-cover" /> : <UserIcon size={32} className="text-gray-400" />}
                                   <div className="absolute bottom-0 right-0 p-1 rounded-tl-xl bg-amber-500">
                                      <ShieldAlert size={12} className="text-white" />
                                   </div>
                                </div>
                                <div>
                                   <div className="flex items-center gap-2 flex-wrap">
                                      <h3 className={`text-[15px] font-black uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{u.displayName}</h3>
                                       {u.internalTestingBypass && (
                                          <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-300 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-700">
                                             PRUEBA INTERNA
                                          </span>
                                       )}
                                      {u.isBlocked && <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[8px] font-black uppercase rounded-full">Bloqueado</span>}
                                      <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-[8px] font-black uppercase rounded-full">Pendiente</span>
                                      {u.aiPreApproved === true && (
                                         <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 text-[8px] font-black uppercase rounded-full border border-emerald-200 dark:border-emerald-800">
                                            <BrainCircuit size={10} /> Pre-aprobado IA
                                         </span>
                                      )}
                                      {u.aiPreApproved === false && (
                                         <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 text-[8px] font-black uppercase rounded-full border border-red-200 dark:border-red-800">
                                            <BrainCircuit size={10} /> Revisión IA
                                         </span>
                                      )}
                                      {u.aiPreApproved === undefined && (
                                         <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-[8px] font-black uppercase rounded-full border border-gray-200 dark:border-gray-700">
                                            <BrainCircuit size={10} /> Sin auditar
                                         </span>
                                      )}
                                   </div>
                                   <p className="text-[10px] font-bold text-indigo-500 uppercase">{u.mainSpecialty || 'Especialidad no definida'}</p>
                                   <div className="flex flex-col gap-1 mt-1">
                                      <div className="flex items-center gap-3">
                                         <a href={u.linkedin || '#'} target="_blank" rel="noopener noreferrer" className="text-[9px] font-black text-blue-500 uppercase hover:underline flex items-center gap-1">
                                            <Globe size={10} /> LinkedIn Profile
                                         </a>
                                         <span className="text-[9px] font-bold text-gray-400 uppercase flex items-center gap-1">
                                            <Building size={10} /> {u.college || u.workplace || 'Sin Institución'}
                                         </span>
                                      </div>
                                      {u.location && u.location.lat !== undefined && u.location.lng !== undefined ? (
                                         <div className="flex items-center gap-1 text-[9px] text-emerald-500 font-bold uppercase transition-all">
                                            <MapPin size={10} /> Ubicación: {u.workCity || ''}, {u.workCountry || ''}
                                         </div>
                                      ) : (
                                         <div className="flex items-center gap-1 text-[9px] text-amber-500 font-bold uppercase transition-all">
                                            <MapPin size={10} strokeWidth={2.5} /> Sin georreferencia de mapa ({u.workCity || 'Sin Ciudad'}, {u.workCountry || u.country || 'Sin País'})
                                         </div>
                                      )}
                                   </div>
                                </div>
                             </div>

                             <div className="flex-1 max-w-md">
                                <div className={`p-4 rounded-2xl text-[10px] font-medium leading-relaxed ${isDarkMode ? 'bg-gray-800/50 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                                   <p className="font-black uppercase text-indigo-500 mb-1 flex items-center gap-1"><BrainCircuit size={12}/> Reporte IA Grounding:</p>
                                   {u.validationReport || "No hay reporte de IA disponible para este usuario."}
                                </div>
                             </div>

                             <div className="flex items-center gap-2 shrink-0">
                                <button 
                                   onClick={() => setProfileViewUid(u.uid)}
                                   className={`p-3 rounded-2xl transition-all ${isDarkMode ? 'bg-indigo-950/40 text-indigo-400 hover:bg-indigo-900/40 border border-indigo-800' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200'}`}
                                   title="Ver Ficha Completa del Perfil"
                                >
                                   <Eye size={18} />
                                </button>
                                <button 
                                   onClick={() => handleAiAudit(u)}
                                   disabled={isAuditing === u.uid}
                                   className={`flex items-center gap-2 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 ${isAuditing === u.uid ? 'bg-gray-600 text-gray-400' : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/20'}`}
                                   title="Ejecutar Auditoría IA Proactiva"
                                >
                                   {isAuditing === u.uid ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />} 
                                   Auditoría IA
                                </button>

                                <button 
                                   onClick={() => handleManualValidation(u.uid, true)}
                                   className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-emerald-600/20 active:scale-95"
                                >
                                   <UserCheck size={16} /> Validar
                                </button>
                                
                                <button 
                                   onClick={() => handleToggleBlock(u.uid, !u.isBlocked)}
                                   className={`p-3 rounded-2xl transition-all ${u.isBlocked ? 'bg-gray-200 text-gray-600 hover:bg-gray-300' : 'bg-red-50 text-red-600 border border-red-100 hover:bg-red-100'}`}
                                   title={u.isBlocked ? 'Desbloquear' : 'Bloquear'}
                                >
                                   {u.isBlocked ? <UserPlus size={18} /> : <UserMinus size={18} />}
                                </button>
                                <button 
                                   onClick={() => setDeleteConfirmUser({ uid: u.uid, displayName: u.displayName })}
                                   className="p-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl transition-all shadow-lg shadow-red-600/20 active:scale-95"
                                   title="Eliminar usuario por completo (podrá volver a registrarse con el mismo correo)"
                                >
                                   <Trash2 size={18} />
                                </button>
                             </div>
                          </div>
                       </div>
                    ))}
                    {users.filter(u => !u.isVerified).length === 0 && (
                       <div className="py-10 flex flex-col items-center justify-center opacity-30 border-2 border-dashed border-gray-200 rounded-[32px]">
                          <CheckCircle size={32} className="text-emerald-500 mb-2" />
                          <p className="text-[10px] font-black uppercase tracking-widest">Todos los usuarios están identificados</p>
                       </div>
                    )}
                 </div>
              </div>

              {/* SECCIÓN: USUARIOS VALIDADOS */}
              <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-gray-800">
                 <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-6 bg-emerald-500 rounded-full"></div>
                    <h3 className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Comunidad Validada</h3>
                 </div>
                 <div className="grid grid-cols-1 gap-4">
                    {users.filter(u => u.isVerified).length === 0 && (
                       <div className={`p-12 rounded-[32px] border border-dashed flex flex-col items-center justify-center text-center ${isDarkMode ? 'bg-gray-800/20 border-gray-700' : 'bg-gray-50/50 border-gray-200'}`}>
                          <ShieldCheck size={48} className="text-gray-300 mb-4" />
                          <p className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No hay usuarios validados en la comunidad</p>
                          <p className="text-[10px] font-bold text-gray-400 mt-2 uppercase">Los usuarios aparecerán aquí una vez que su identidad sea confirmada</p>
                       </div>
                    )}
                    {users.filter(u => u.isVerified).map(u => (
                       <div key={u.uid} className={`p-6 rounded-[32px] border transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100 shadow-sm opacity-80 hover:opacity-100'}`}>
                          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                             <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden relative">
                                   {u.photoURL ? <img src={u.photoURL} className="w-full h-full object-cover" /> : <UserIcon size={32} className="text-gray-400" />}
                                   <div className="absolute bottom-0 right-0 p-1 rounded-tl-xl bg-emerald-500">
                                      <ShieldCheck size={12} className="text-white" />
                                   </div>
                                </div>
                                <div>
                                   <div className="flex items-center gap-2 flex-wrap">
                                      <h3 className={`text-[15px] font-black uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{u.displayName}</h3>
                                       {u.internalTestingBypass && (
                                          <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-300 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-700">
                                             PRUEBA INTERNA
                                          </span>
                                       )}
                                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-600 text-[8px] font-black uppercase rounded-full">Validado</span>
                                   </div>
                                   <p className="text-[10px] font-bold text-indigo-500 uppercase">{u.mainSpecialty}</p>
                                   {u.location && u.location.lat !== undefined && u.location.lng !== undefined ? (
                                      <div className="flex items-center gap-1 text-[9px] text-emerald-500 font-bold uppercase mt-1">
                                         <MapPin size={10} /> Ubicación: {u.workCity || ''}, {u.workCountry || u.country || ''}
                                      </div>
                                   ) : (
                                      <div className="flex items-center gap-1 text-[9px] text-gray-400 font-bold uppercase mt-1">
                                         <MapPin size={10} strokeWidth={2} /> Sin georreferencia ({u.workCity || 'Sin Ciudad'}, {u.workCountry || u.country || 'Sin País'})
                                      </div>
                                    )}
                                </div>
                             </div>

                             <div className="flex items-center gap-2 shrink-0">
                                <button 
                                   onClick={() => setProfileViewUid(u.uid)}
                                   className={`p-3 rounded-2xl transition-all ${isDarkMode ? 'bg-indigo-950/40 text-indigo-400 hover:bg-indigo-900/40 border border-indigo-800' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200'}`}
                                   title="Ver Ficha Completa del Perfil"
                                >
                                   <Eye size={18} />
                                </button>
                                {u.location?.lat === -999 && (
                                   <button 
                                      onClick={() => {
                                         setMapFocusUser(u);
                                         setMapOnlyVerified(false);
                                         setActiveTab('NETWORK');
                                      }}
                                      className="p-3 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded-2xl border border-blue-200 dark:border-blue-800 transition-all flex items-center justify-center gap-1.5"
                                      title="Ver Georreferenciación en Mapa"
                                   >
                                      <MapIcon size={16} />
                                      <span className="text-[10px] font-black uppercase tracking-widest hidden md:inline">Mapa</span>
                                   </button>
                                )}
                                {checkProfileIncomplete(u) && (
                                   <button 
                                      onClick={() => handleToggleBypass(u.uid, !u.bypassProfileLock)}
                                      className={`relative p-3 rounded-2xl transition-all ${u.bypassProfileLock ? 'bg-gray-100 text-gray-400 border border-gray-200' : 'bg-amber-100 text-amber-600 border border-amber-200 shadow-md'}`}
                                      title={u.bypassProfileLock ? 'Aviso bloqueado: no se le exigirán los datos faltantes (clic para reactivar)' : 'Aviso: tiene datos de perfil por completar (clic para bloquear este aviso)'}
                                   >
                                      <AlertTriangle size={18} />
                                      {u.bypassProfileLock && (
                                         <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                            <span className="w-[22px] h-[2px] bg-current rotate-45 rounded-full"></span>
                                         </span>
                                      )}
                                   </button>
                                )}
                                <button 
                                   onClick={() => handleAiAudit(u)}
                                   disabled={isAuditing === u.uid}
                                   className={`flex items-center gap-2 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${isAuditing === u.uid ? 'bg-gray-600 text-gray-400' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                >
                                   {isAuditing === u.uid ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />} 
                                   Re-Validar IA
                                </button>

                                <button 
                                   onClick={() => handleManualValidation(u.uid, false)}
                                   className="flex items-center gap-2 px-6 py-3 bg-amber-100 text-amber-600 hover:bg-amber-200 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
                                >
                                   <ShieldAlert size={16} /> Revocar
                                </button>
                             </div>
                          </div>
                       </div>
                    ))}
                 </div>
              </div>
           </div>
        )}

        {activeTab === 'ERROR_LOGS' && (
          <div className="space-y-10 animate-in fade-in">
             <div className="flex items-center justify-between mb-2">
                <div>
                   <h2 className={`text-2xl font-black tracking-tighter uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Análisis Técnico de Fallos</h2>
                   <p className="text-[10px] font-black text-red-500 uppercase tracking-widest">Auditoría de Errores en Tiempo Real</p>
                </div>
                <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 px-4 py-2 rounded-2xl border border-red-100 dark:border-red-900/30">
                    <AlertTriangle size={14} className="text-red-500" />
                    <span className="text-[10px] font-black text-red-600 uppercase tracking-widest">{appLogs.length} Entradas</span>
                </div>
             </div>

             <div className="grid grid-cols-1 gap-6">
                {appLogs.map((log) => (
                   <div key={log.id} className={`p-6 rounded-[32px] border transition-all hover:shadow-md ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                      <div className="flex items-start justify-between gap-4 mb-4">
                         <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-2xl ${log.severity === 'critical' ? 'bg-red-100 text-red-600' : log.severity === 'high' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                               {log.severity === 'critical' ? <ShieldAlert size={20}/> : <AlertCircle size={20}/>}
                            </div>
                            <div>
                               <p className={`text-xs font-black uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{log.errorMessage}</p>
                               <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">{log.userEmail || 'Anónimo'}</span>
                                  <span className="text-[10px] font-medium text-gray-400">•</span>
                                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-widest">
                                    {log.timestamp?.toDate ? log.timestamp.toDate().toLocaleString() : new Date(log.timestamp).toLocaleString()}
                                  </span>
                               </div>
                            </div>
                         </div>
                         <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest ${
                            log.severity === 'critical' ? 'bg-red-600 text-white shadow-lg shadow-red-600/20' :
                            log.severity === 'high' ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' :
                            'bg-blue-500 text-white'
                         }`}>
                            {log.severity}
                         </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
                         <div className="space-y-4">
                            <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5"><Terminal size={14}/> Stack Trace / Contexto</h5>
                            <div className={`p-4 rounded-2xl font-mono text-[9px] overflow-auto max-h-40 break-all transition-colors ${isDarkMode ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                               {log.stack || 'Sin rastro de pila disponible'}
                            </div>
                         </div>
                         <div className="space-y-3">
                            <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5"><Activity size={14}/> Detalles del Entorno</h5>
                            <div className="grid grid-cols-2 gap-3">
                               <LogDetail label="Operación" value={log.operationType || 'N/A'} isDark={isDarkMode} />
                               <LogDetail label="Ruta" value={log.path || 'N/A'} isDark={isDarkMode} />
                               <LogDetail label="Plataforma" value={log.deviceInfo?.platform || 'N/A'} isDark={isDarkMode} />
                               <LogDetail label="Pantalla" value={log.deviceInfo?.screenSize || 'N/A'} isDark={isDarkMode} />
                            </div>
                            <div className="mt-2 p-3 rounded-xl bg-indigo-50/50 dark:bg-indigo-900/10 border border-indigo-100/50 dark:border-indigo-900/30">
                               <p className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter truncate">User Agent: {log.deviceInfo?.userAgent}</p>
                            </div>
                         </div>
                      </div>
                   </div>
                ))}
                {appLogs.length === 0 && (
                   <div className="flex flex-col items-center justify-center py-24 opacity-30">
                      <AlertTriangle size={64} className="mb-4" />
                      <p className="text-sm font-black uppercase tracking-widest">Sin registros de errores detectados</p>
                      <p className="text-xs font-bold uppercase mt-2">La aplicación se encuentra estable</p>
                   </div>
                )}
             </div>
          </div>
        )}

        {activeTab === 'LATENCY_CONTROL' && (
          <div className="space-y-10 animate-in fade-in">
             <div className="flex items-center justify-between mb-2">
                <div>
                   <h2 className={`text-2xl font-black tracking-tighter uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Control de Latencia de Sistema</h2>
                   <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Monitoreo Técnico PhD en Tiempo Real</p>
                </div>
                <div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                    <Timer size={14} className="text-indigo-500" />
                    <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Midiendo: {latencyEvents.length} Eventos</span>
                </div>
             </div>

             {/* DASHBOARD SUMMARY */}
             {latencyStats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                   {Object.entries(latencyStats).map(([id, stats]) => (
                      <div key={id} className={`p-6 rounded-[32px] border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                         <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-2xl ${isDarkMode ? 'bg-indigo-900/30' : 'bg-indigo-50'}`}>
                               {id === LatencyModule.TRANSCRIPTION ? <Mic size={20} className="text-indigo-500" /> : <Cpu size={20} className="text-indigo-500" />}
                            </div>
                            <span className={`text-[10px] font-black px-3 py-1 rounded-full ${stats.errors > 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                               {stats.errors} FALLOS
                            </span>
                         </div>
                         <h3 className={`text-xs font-black uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stats.label}</h3>
                         <div className="flex items-baseline gap-2 mt-2">
                            <span className={`text-3xl font-black tracking-tighter ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{(stats.avg / 1000).toFixed(2)}s</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase">media</span>
                         </div>
                         <div className="flex items-baseline gap-2 mt-1">
                            <span className={`text-sm font-black ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{(stats.max / 1000).toFixed(2)}s</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase">max</span>
                         </div>
                      </div>
                   ))}
                </div>
             )}

             {/* CHARTS */}
             <div className="grid grid-cols-1 gap-6">
                <div className={`p-8 rounded-[40px] border ${isDarkMode ? 'bg-gray-800/20 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                    <h3 className={`text-sm font-black uppercase tracking-widest mb-8 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Tendencia de Respuesta (Inferencia IA)</h3>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={latencyChartData}>
                                <defs>
                                    <linearGradient id="latencyGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#374151' : '#f3f4f6'} />
                                <XAxis dataKey="time" axisLine={false} tickLine={false} interval="preserveStartEnd"  stroke={isDarkMode ? '#6b7280' : '#9ca3af'} fontSize={10} hide />
                                <YAxis 
                                    stroke={isDarkMode ? '#9ca3af' : '#6b7280'} 
                                    fontSize={10} 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tickFormatter={(val) => `${(val / 1000).toFixed(1)}s`} 
                                />
                                <Tooltip 
                                    contentStyle={{ borderRadius: '20px', border: 'none', backgroundColor: isDarkMode ? '#1f2937' : 'white', color: isDarkMode ? 'white' : 'black', fontSize: '10px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    formatter={(val: number) => [`${(val / 1000).toFixed(2)}s`, 'Duración']}
                                />
                                <Area animationDuration={1000} type="monotone" dataKey="ms" stroke="#6366f1" strokeWidth={4} fillOpacity={1} fill="url(#latencyGradient)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
             </div>

             {/* ERROR LOG - ANÁLISIS FORENSE */}
             <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-6 bg-red-500 rounded-full"></div>
                    <h3 className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Análisis Forense de Fallos Técinicos</h3>
                </div>
                <div className={`rounded-[32px] border overflow-hidden ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className={`${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Módulo</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Estado</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Duración</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Error Técnico</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Metadata / Payload</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {latencyEvents.filter(e => e.status !== 'SUCCESS').slice(0, 20).map(e => (
                                <tr key={e.id} className="hover:bg-red-500/5 transition-colors">
                                    <td className="px-6 py-4">
                                        <span className="text-[11px] font-black uppercase text-indigo-500">{e.module}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-red-100 text-red-600 uppercase">{e.status}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`text-[11px] font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>{(e.durationMs / 1000).toFixed(2)}s</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-[10px] font-black text-red-500 uppercase">{e.errorCode || 'TIMEOUT_GENERIC'}</span>
                                            <span className="text-[9px] font-medium text-gray-400 max-w-xs truncate">{e.errorMessage || 'Sin mensaje de sistema de bajo nivel'}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">Payload: {e.payloadSize ? (e.payloadSize / 1024).toFixed(1) : 0} KB</span>
                                            {e.metadata?.model && <span className="text-[8px] font-black text-indigo-400">Model: {e.metadata.model}</span>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {latencyEvents.filter(e => e.status !== 'SUCCESS').length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                        No se han detectado anomalías técnicas en los últimos registros
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
             </div>
          </div>
        )}

        {activeTab === 'DELEGATION' && (
           <div className="space-y-10 animate-in fade-in">
              <div className="flex items-center justify-between mb-2">
                 <div>
                    <h2 className={`text-2xl font-black tracking-tighter uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Delegación Coexistente</h2>
                    <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Co-Administradores del Sistema (Opción B)</p>
                 </div>
                 <div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                     <ShieldCheck size={14} className="text-indigo-500" />
                     <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Coexistente activo</span>
                 </div>
              </div>

              {/* SECCIÓN 1: Formulario de Adición / Delegación */}
              <div className={`p-8 rounded-[40px] border shadow-xl transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
                <h3 className="text-xs font-black uppercase tracking-widest text-indigo-500 mb-2">Asignar Nuevo Administrador</h3>
                <p className={`text-xs mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Ingrese el correo de un radiólogo ya validado en el sistema para conferirle privilegios de administrador coexistente. Ambos tendrán facultades de administración de forma simultánea (Opción B).
                </p>

                {delegationMessage && (
                  <div className={`p-4 rounded-2xl mb-6 flex items-center gap-3 border text-xs font-semibold ${
                    delegationMessage.type === 'success' 
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500' 
                      : 'bg-red-500/10 border-red-500/30 text-red-500'
                  }`}>
                    {delegationMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                    <span>{delegationMessage.text}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Formulario Izquierda */}
                  <div className="space-y-5">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Seleccionar especialista verificado</label>
                      <select 
                        value={delegationEmail} 
                        onChange={(e) => setDelegationEmail(e.target.value)}
                        className={`w-full p-4 rounded-2xl border text-xs font-medium outline-none transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900 focus:bg-white'}`}
                      >
                        <option value="">-- Elige un usuario verificado --</option>
                        {users.filter(u => u.isVerified && u.email?.toLowerCase().trim() !== 'dr.francisco.reyes@gmail.com').map(u => (
                          <option key={u.uid} value={u.email}>
                            {u.displayName || 'Radiólogo'} ({u.email || 'Sin correo'}) - {u.mainSpecialty || 'Especialista'}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400">O ingresar correo manualmente</label>
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Ej. especialista@gmail.com</span>
                      </div>
                      <input 
                        type="email" 
                        value={delegationEmail} 
                        onChange={(e) => setDelegationEmail(e.target.value)}
                        placeholder="doctor@workspace.com" 
                        className={`w-full p-4 rounded-2xl border text-xs font-medium outline-none transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900 focus:bg-white'}`}
                      />
                    </div>

                    <button 
                      onClick={handleDelegate}
                      disabled={delegationLoading || !delegationEmail.trim()}
                      className={`w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl transition-all flex items-center justify-center gap-2 ${
                        delegationLoading || !delegationEmail.trim()
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 shadow-indigo-600/20'
                      }`}
                    >
                      {delegationLoading ? <RefreshCw size={14} className="animate-spin" /> : <ShieldCheck size={14} />} 
                      Conferir Rol de Co-Administrador
                    </button>
                  </div>

                  {/* Diagnóstico Derecha */}
                  <div className={`p-6 rounded-3xl border ${isDarkMode ? 'bg-black/20 border-gray-800' : 'bg-gray-50 border-gray-100'}`}>
                    <h4 className="text-[10px] font-black uppercase tracking-wider text-indigo-500 mb-4 flex items-center gap-2">
                      <Radar size={14} /> Información del Sistema (Modo Coexistente)
                    </h4>
                    <ul className={`space-y-3 text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <li className="flex justify-between py-1.5 border-b border-gray-100/10">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-wide">Administrador Primario:</span>
                        <span className="font-bold">dr.francisco.reyes@gmail.com</span>
                      </li>
                      <li className="flex justify-between py-1.5 border-b border-gray-100/10">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-wide">Tipo de Delegación:</span>
                        <span className="font-bold text-indigo-500">OPCIÓN B (COEXISTE)</span>
                      </li>
                      <li className="flex justify-between py-1.5 border-b border-gray-100/10">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-wide">Co-Admins Autorizados:</span>
                        <span className="font-bold">{(delegation?.delegatedEmails || []).length} especialistas</span>
                      </li>
                      <li className="flex justify-between py-1.5">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-wide">Privilegios Concedidos:</span>
                        <span className="font-bold text-emerald-500">LECTURA/ESCRITURA TOTAL</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* SECCIÓN 2: Lista de Co-administradores activos */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-4">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 flex items-center gap-2">
                    <Users size={14} /> Administradores Coexistentes Activos
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {users.filter(u => u.role === 'ADMIN' && u.email?.toLowerCase().trim() !== 'dr.francisco.reyes@gmail.com').map(admin => (
                      <div key={admin.uid} className={`p-6 rounded-[32px] border shadow-sm transition-colors flex flex-col justify-between ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
                        <div>
                          <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-indigo-500/10 text-indigo-500 rounded-2xl"><UserIcon size={18} /></div>
                            <span className="text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">ADMIN COEXISTENTE</span>
                          </div>
                          <h4 className={`text-md font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{admin.displayName || 'Especialista'}</h4>
                          <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase">{admin.email}</p>
                          <p className="text-[9px] font-black uppercase tracking-wider text-indigo-500 mt-2">{admin.mainSpecialty || 'Radiólogo'}</p>
                        </div>

                        <div className="mt-6 pt-4 border-t border-gray-100/10 flex justify-end">
                          <button 
                            onClick={() => handleRevoke(admin.email || '', admin.uid)}
                            disabled={delegationLoading}
                            className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest border border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white px-3.5 py-2 rounded-xl transition-all"
                          >
                            <UserMinus size={12} /> Revocar Acceso
                          </button>
                        </div>
                      </div>
                    ))}

                    {users.filter(u => u.role === 'ADMIN' && u.email?.toLowerCase().trim() !== 'dr.francisco.reyes@gmail.com').length === 0 && (
                      <div className={`col-span-2 p-8 rounded-[32px] border border-dashed flex flex-col items-center justify-center py-12 transition-colors ${isDarkMode ? 'bg-black/10 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="p-3 bg-indigo-500/5 text-indigo-400/40 rounded-2xl mb-2"><ShieldAlert size={20} /></div>
                        <p className={`text-xs font-black uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No hay administradores coexistentes delegados</p>
                        <p className="text-[10px] font-bold text-gray-400 mt-1 text-center">SÓLO EL COMPORTAMIENTO PRINCIPAL DEL EXCLUSIVO MASTER (DR. FRANCISCO) ESTÁ OPERANDO.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Historil / Logs */}
                <div className="space-y-4">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 flex items-center gap-2">
                    <FileText size={14} /> Historial de Delegación
                  </h3>

                  <div className={`p-6 rounded-[32px] border h-[320px] overflow-y-auto custom-scrollbar transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
                    {delegation?.history && delegation.history.length > 0 ? (
                      <div className="space-y-4">
                        {delegation.history.slice().reverse().map((h, idx) => (
                          <div key={idx} className="flex gap-3 py-1 items-start text-xs border-b border-gray-100/10 pb-3 last:border-0 last:pb-0">
                            <div className={`p-1.5 rounded-lg shrink-0 mt-0.5 ${h.action === 'GRANTED' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                              {h.action === 'GRANTED' ? <UserPlus size={12} /> : <UserMinus size={12} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-bold truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{h.email}</p>
                              <p className="text-[10px] font-black tracking-widest text-gray-400 uppercase mt-0.5">
                                Accion: <span className={h.action === 'GRANTED' ? 'text-emerald-500' : 'text-red-500'}>{h.action === 'GRANTED' ? 'CONCEDIDO' : 'REVOCADO'}</span>
                              </p>
                              <p className="text-[8px] font-bold text-gray-500 uppercase mt-0.5">{new Date(h.timestamp).toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full opacity-30 text-center">
                        <Calendar size={32} className="mb-2" />
                        <p className="text-[10px] font-black uppercase tracking-widest">Sin registros históricos</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
           </div>
        )}

        {activeTab === 'REGIONAL_CONTROL' && (
           <div className="space-y-10 animate-in fade-in">
              <div className="flex items-center justify-between mb-2">
                 <div>
                    <h2 className={`text-2xl font-black tracking-tighter uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Control Regional de Módulos</h2>
                    <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Restricciones de Visibilidad para Especialistas</p>
                 </div>
                 <div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                     <Globe size={14} className="text-indigo-500" />
                     <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{regionalRestrictions.length} Reglas Activas</span>
                 </div>
              </div>

              {/* Formulario de Configuración de Nueva Regla o Edición */}
              <div 
                ref={ruleFormRef}
                className={`p-8 rounded-[40px] border shadow-xl transition-all ${
                  editingRuleId 
                    ? isDarkMode 
                      ? 'bg-[#2a2d34] border-indigo-500/80 ring-2 ring-indigo-500/30' 
                      : 'bg-indigo-50/40 border-indigo-300 ring-2 ring-indigo-500/20'
                    : isDarkMode 
                      ? 'bg-[#2a2d34] border-gray-700' 
                      : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2.5">
                    <span className={`p-2 rounded-xl ${editingRuleId ? 'bg-indigo-600 text-white shadow-md' : 'bg-indigo-500/10 text-indigo-500'}`}>
                      {editingRuleId ? <Pencil size={16} /> : <Globe size={16} />}
                    </span>
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-widest text-[#6366f1]">
                        {editingRuleId ? `Modificando Restricción: ${ruleForm.value || 'Seleccionada'}` : 'Configurar Nueva Limitación'}
                      </h3>
                      {editingRuleId && (
                        <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">
                          Modo Edición Activo • Modifica los módulos autorizados o políticas y guarda los cambios
                        </p>
                      )}
                    </div>
                  </div>

                  {editingRuleId && (
                    <button
                      type="button"
                      onClick={handleCancelEditRegionalRule}
                      className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border flex items-center gap-1.5 self-start sm:self-auto ${
                        isDarkMode
                          ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <X size={12} />
                      <span>Cancelar Edición</span>
                    </button>
                  )}
                </div>

                <p className={`text-xs mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Elige una región (continente) o introduce un país. Activa únicamente las casillas de los módulos que deseas que sean <strong>VISIBLES</strong> para los usuarios de esa localización geográfica.
                </p>

                {regionalMessage && (
                  <div className={`p-4 rounded-2xl mb-6 flex items-center gap-3 border text-xs font-semibold ${
                    regionalMessage.type === 'success' 
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500' 
                      : 'bg-red-500/10 border-red-500/30 text-red-500'
                  }`}>
                    {regionalMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                    <span>{regionalMessage.text}</span>
                  </div>
                )}

                <form onSubmit={handleSaveRegionalRule} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Selectores de Regla */}
                    <div className="space-y-6">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Tipo de Regulación</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setRuleForm(prev => ({ ...prev, type: 'CONTINENT', value: 'Europa' }))}
                            className={`py-3 px-4 rounded-2xl text-[10px] font-black tracking-widest uppercase transition-all border ${
                              ruleForm.type === 'CONTINENT'
                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg'
                                : isDarkMode
                                  ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            Por Región (Continente)
                          </button>
                          <button
                            type="button"
                            onClick={() => setRuleForm(prev => ({ ...prev, type: 'COUNTRY', value: 'España' }))}
                            className={`py-3 px-4 rounded-2xl text-[10px] font-black tracking-widest uppercase transition-all border ${
                              ruleForm.type === 'COUNTRY'
                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg'
                                : isDarkMode
                                  ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            Por País Específico
                          </button>
                        </div>
                      </div>

                      {ruleForm.type === 'CONTINENT' ? (
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Seleccionar Región (Continente)</label>
                          <select
                            value={ruleForm.value}
                            onChange={(e) => setRuleForm(prev => ({ ...prev, value: e.target.value }))}
                            className={`w-full p-4 rounded-2xl border text-xs font-medium outline-none transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900 focus:bg-white'}`}
                          >
                            <option value="Europa">Europa</option>
                            <option value="Norteamérica">Norteamérica</option>
                            <option value="Sudamérica">Sudamérica</option>
                          </select>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Seleccionar o Escribir País</label>
                            <select
                              onChange={(e) => setRuleForm(prev => ({ ...prev, value: e.target.value }))}
                              value={['España', 'Argentina', 'Colombia', 'Chile', 'México', 'Perú', 'Ecuador', 'Estados Unidos', 'Uruguay'].includes(ruleForm.value) ? ruleForm.value : ""}
                              className={`w-full p-4 rounded-2xl border text-xs font-medium outline-none transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900 focus:bg-white'}`}
                            >
                              <option value="">-- Elige un país común o escribe abajo --</option>
                              <option value="España">España</option>
                              <option value="Argentina">Argentina</option>
                              <option value="Colombia">Colombia</option>
                              <option value="Chile">Chile</option>
                              <option value="México">México</option>
                              <option value="Perú">Perú</option>
                              <option value="Ecuador">Ecuador</option>
                              <option value="Estados Unidos">Estados Unidos</option>
                              <option value="Uruguay">Uruguay</option>
                            </select>
                          </div>
                          <div>
                            <input
                              type="text"
                              required
                              value={ruleForm.value}
                              onChange={(e) => setRuleForm(prev => ({ ...prev, value: e.target.value }))}
                              placeholder="Ej. España, Francia, Colombia..."
                              className={`w-full p-4 rounded-2xl border text-xs font-medium outline-none transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-100 text-gray-900 focus:bg-white focus:border-indigo-400'}`}
                            />
                          </div>
                        </div>
                      )}

                      {/* Políticas de Nomenclatura y Bloqueo Clínico */}
                      <div className={`p-5 rounded-3xl border ${isDarkMode ? 'bg-black/15 border-gray-850' : 'bg-gray-50 border-gray-200'} space-y-4`}>
                        <h4 className="text-[10px] font-black uppercase tracking-wider text-indigo-500">Políticas Clínicas Especiales</h4>
                        
                        <div className="space-y-3">
                          {/* Cambiar "Conclusión" por "Resumen de hallazgos principales" */}
                          <label className="flex items-start gap-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={ruleForm.renameConclusionToSummary}
                              onChange={(e) => setRuleForm(prev => ({ ...prev, renameConclusionToSummary: e.target.checked }))}
                              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4 shrink-0"
                            />
                            <div>
                              <span className={`text-[11px] font-black uppercase tracking-wide block ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                Activar Cambio de Término
                              </span>
                              <span className="text-[9px] font-medium text-gray-400 dark:text-gray-500 block leading-tight mt-0.5">
                                Cambia el título de la sección de "Conclusión" a "Resumen de hallazgos principales" en las cajas de respuesta.
                              </span>
                            </div>
                          </label>

                          {/* Bloqueo regional para diagnósticos diferenciales */}
                          <label className="flex items-start gap-3 cursor-pointer group pt-2 border-t border-gray-200/10">
                            <input
                              type="checkbox"
                              checked={ruleForm.disableDiferenciales}
                              onChange={(e) => setRuleForm(prev => ({ ...prev, disableDiferenciales: e.target.checked }))}
                              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4 shrink-0"
                            />
                            <div>
                              <span className={`text-[11px] font-black uppercase tracking-wide block ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                Bloquear Emisión de Diferenciales
                              </span>
                              <span className="text-[9px] font-medium text-gray-400 dark:text-gray-500 block leading-tight mt-0.5">
                                Desactiva o restringe por completo la generación y visualización de diagnósticos diferenciales para esta región.
                              </span>
                            </div>
                          </label>
                        </div>
                      </div>

                      <div className="pt-4 flex gap-2">
                        <button
                          type="submit"
                          className="flex-1 py-4 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-xl shadow-indigo-600/20 flex items-center justify-center gap-2"
                        >
                          <Save size={14} />
                          {editingRuleId ? 'Guardar Cambios de la Regla' : 'Guardar Regla de Control Regional'}
                        </button>
                        {editingRuleId && (
                          <button
                            type="button"
                            onClick={handleCancelEditRegionalRule}
                            className={`px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                              isDarkMode
                                ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                                : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            Cancelar
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Checkboxes de módulos */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 font-semibold">Módulos Visibles (Autorizados)</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setRuleForm(prev => ({ ...prev, disabledModules: [] }))}
                            className="text-[9px] font-black text-indigo-500 uppercase tracking-wider hover:underline"
                          >
                            Autorizar Todos
                          </button>
                          <span className="text-gray-400 text-[9px] font-black">•</span>
                          <button
                            type="button"
                            onClick={() => setRuleForm(prev => ({ ...prev, disabledModules: Object.keys(REGIONAL_MODULES_MAP) }))}
                            className="text-[9px] font-black text-amber-600 uppercase tracking-wider hover:underline"
                          >
                            Bloquear Todos
                          </button>
                        </div>
                      </div>

                      <div className={`grid grid-cols-2 gap-2 max-h-[280px] overflow-y-auto p-4 rounded-3xl border custom-scrollbar ${isDarkMode ? 'bg-black/20 border-gray-800' : 'bg-gray-50 border-gray-100'}`}>
                        {Object.entries(REGIONAL_MODULES_MAP).map(([viewId, label]) => {
                          const isVisible = !ruleForm.disabledModules.includes(viewId);
                          return (
                            <button
                              key={viewId}
                              type="button"
                              onClick={() => handleToggleModuleInRule(viewId)}
                              className={`p-3.5 rounded-2xl border text-left transition-all flex items-center gap-3 ${
                                isVisible
                                  ? isDarkMode
                                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 font-bold'
                                    : 'bg-emerald-50 border-emerald-100 text-emerald-700 font-bold shadow-sm'
                                  : isDarkMode
                                    ? 'bg-gray-800/40 border-gray-800 text-gray-500'
                                    : 'bg-white border-gray-100 text-gray-400 hover:bg-gray-50 font-semibold'
                              }`}
                            >
                              <div className={`w-4 h-4 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all ${
                                isVisible ? 'bg-emerald-500 border-emerald-500 text-white border-transparent' : 'border-gray-300 dark:border-gray-700'
                              }`}>
                                {isVisible && <Check size={10} strokeWidth={4} />}
                              </div>
                              <span className="text-[10px] font-black uppercase tracking-tight truncate">{label}</span>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[9px] font-bold text-gray-400 uppercase mt-3 text-right">
                        Los módulos seleccionados estarán habilitados. Los no seleccionados se ocultarán en el workspace.
                      </p>
                    </div>
                  </div>
                </form>
              </div>

              {/* Listado de Reglas Activas */}
              <div className="space-y-4">
                 <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 flex items-center gap-2">
                    <Users size={14} /> Reglas Regionales Activadas en el Sistema
                 </h3>

                 {loadingRegionalRestrictions ? (
                    <div className="flex flex-col items-center justify-center py-12">
                       <RefreshCw className="animate-spin text-indigo-500 mb-2" size={24} />
                       <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sincronizando reglas con Firestore...</span>
                    </div>
                 ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                       {regionalRestrictions.map((rule) => {
                          const visibleModules = Object.keys(REGIONAL_MODULES_MAP).filter(id => !(rule.disabledModules || []).includes(id));
                          const disabledCount = (rule.disabledModules || []).filter(id => Object.keys(REGIONAL_MODULES_MAP).includes(id)).length;
                          
                          return (
                             <div 
                                key={rule.id} 
                                className={`p-6 rounded-[32px] border shadow-sm transition-all flex flex-col justify-between ${
                                   editingRuleId === rule.id
                                      ? isDarkMode 
                                         ? 'bg-[#2a2d34] border-indigo-500 ring-2 ring-indigo-500/40' 
                                         : 'bg-indigo-50/20 border-indigo-400 ring-2 ring-indigo-400/30'
                                      : isDarkMode 
                                         ? 'bg-[#2a2d34] border-gray-700' 
                                         : 'bg-white border-gray-100'
                                }`}
                             >
                                <div className="space-y-4">
                                   <div className="flex justify-between items-start">
                                      <div className="flex items-center gap-3">
                                         <div className={`p-3 rounded-2xl ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'} text-indigo-500 shadow-sm`}>
                                            {rule.type === 'CONTINENT' ? <Globe size={20} /> : <MapPin size={20} />}
                                         </div>
                                         <div>
                                            <span className="text-[8px] font-black uppercase tracking-widest text-[#71747a]">
                                               {rule.type === 'CONTINENT' ? 'Restricción por Región' : 'Restricción por País'}
                                            </span>
                                            <h4 className={`text-lg font-black tracking-tight uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                               {rule.value}
                                            </h4>
                                         </div>
                                      </div>
                                      
                                      <div className="flex items-center gap-1.5">
                                         <button
                                            type="button"
                                            onClick={() => handleStartEditRegionalRule(rule)}
                                            className={`px-2.5 py-1.5 border rounded-xl transition-all flex items-center gap-1 text-[9px] font-black uppercase tracking-wider ${
                                               editingRuleId === rule.id
                                                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                                  : isDarkMode
                                                     ? 'border-indigo-500/30 text-indigo-400 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'
                                                     : 'border-indigo-200 text-indigo-600 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'
                                            }`}
                                            title="Editar restricciones de esta regla"
                                         >
                                            <Pencil size={12} />
                                            <span>EDITAR</span>
                                         </button>
                                         
                                         <button
                                            type="button"
                                            onClick={() => handleDeleteRegionalRule(rule.id)}
                                            className="p-2 border border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white rounded-xl transition-all"
                                            title="Eliminar regla regional"
                                         >
                                            <X size={14} />
                                         </button>
                                      </div>
                                   </div>

                                   <div className="space-y-2">
                                      <div className="flex justify-between items-center">
                                         <span className="text-[9px] font-black text-emerald-500 uppercase tracking-wider">Módulos Visibles ({visibleModules.length}):</span>
                                         <span className="text-[9px] font-black text-red-400 uppercase tracking-wider">Bloqueados ({disabledCount})</span>
                                      </div>
                                      
                                      <div className="flex flex-wrap gap-1">
                                         {visibleModules.length === 0 ? (
                                            <span className="text-[9px] font-black uppercase tracking-wider text-red-500 bg-red-500/10 px-2 py-0.5 rounded-md border border-red-500/20">
                                               NINGÚN MÓDULO AUTORIZADO (BLOQUEO TOTAL)
                                            </span>
                                         ) : (
                                            visibleModules.map(mId => (
                                               <span 
                                                  key={mId} 
                                                  className={`text-[8px] font-black uppercase tracking-wide px-2 py-0.5 rounded-lg border ${
                                                     isDarkMode 
                                                        ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400 font-extrabold' 
                                                        : 'bg-emerald-50 border-emerald-100/50 text-emerald-700 font-extrabold'
                                                  }`}
                                               >
                                                  {REGIONAL_MODULES_MAP[mId] || mId}
                                               </span>
                                            ))
                                         )}
                                      </div>
                                   </div>
                                </div>

                                {/* Políticas Especiales Activas */}
                                    {(rule.renameConclusionToSummary || rule.disableDiferenciales) && (
                                       <div className="mt-4 p-4 rounded-2xl border border-gray-100/10 text-left space-y-2 bg-[#1e2024]/10">
                                          <span className="text-[8px] font-black uppercase tracking-wider text-indigo-400 block">Políticas Clínicas Activas:</span>
                                          <div className="flex flex-col gap-1.5">
                                             {rule.renameConclusionToSummary && (
                                                <div className="flex items-center gap-1.5 text-[9px] font-bold text-indigo-500 dark:text-indigo-400 uppercase">
                                                   <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                                                   <span>Término: "Resumen de hallazgos principales"</span>
                                                </div>
                                             )}
                                             {rule.disableDiferenciales && (
                                                <div className="flex items-center gap-1.5 text-[9px] font-bold text-amber-500 dark:text-amber-400 uppercase">
                                                   <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                                   <span>Bloqueo de Diferenciales</span>
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    )}

                                 <div className="mt-6 pt-4 border-t border-gray-100/10 flex justify-between items-center text-[9px] font-black text-[#85888e] uppercase tracking-wider">
                                   <span>Actualizada en Vivo</span>
                                   <span>{new Date(rule.updatedAt || Date.now()).toLocaleDateString()}</span>
                                </div>
                             </div>
                          );
                       })}

                       {regionalRestrictions.length === 0 && (
                          <div className={`col-span-2 p-12 rounded-[40px] border border-dashed flex flex-col items-center justify-center py-16 transition-colors ${isDarkMode ? 'bg-black/10 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                             <div className="p-4 bg-indigo-500/5 text-indigo-400/40 rounded-[24px] mb-3">
                                <Globe size={28} />
                             </div>
                             <p className={`text-xs font-black uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                No hay reglas de restricción regional creadas
                             </p>
                             <p className="text-[10px] font-bold text-gray-400 mt-1 min-w-xs text-center uppercase tracking-wide max-w-sm">
                                Todos los usuarios a nivel mundial visualizarán libremente todos los módulos habilitados por defecto.
                             </p>
                          </div>
                       )}
                    </div>
                 )}
              </div>

               {/* Excepciones de Pruebas Internas por Usuario Individual */}
               <div className={`p-8 rounded-[40px] border shadow-xl transition-all space-y-6 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                     <div>
                        <div className="flex items-center gap-2 mb-1">
                           <span className="p-2 rounded-xl bg-indigo-500/10 text-indigo-500">
                              <FlaskConical size={18} />
                           </span>
                           <h3 className={`text-sm font-black tracking-tight uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Excepciones por Colaborador (Bypass de Restricciones Regionales)
                           </h3>
                        </div>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                           Permite a usuarios colaboradores específicos omitir todas las restricciones geográficas y acceder al conjunto completo de funciones de la aplicación para fines de validación técnica y desarrollo.
                        </p>
                     </div>
                     <div className="flex items-center gap-2">
                        <span className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-300 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-700 flex items-center gap-1.5">
                           <FlaskConical size={12} />
                           {users.filter(u => u.internalTestingBypass).length} con Acceso Completo Activo
                        </span>
                     </div>
                  </div>

                  {/* Buscador de Usuarios */}
                  <div className="relative">
                     <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                     <input
                        type="text"
                        value={internalTestingSearch}
                        onChange={(e) => setInternalTestingSearch(e.target.value)}
                        placeholder="Buscar colaborador por nombre o correo para conceder acceso completo..."
                        className={`w-full pl-11 pr-4 py-3.5 rounded-2xl text-xs font-semibold outline-none transition-all border ${
                           isDarkMode 
                              ? 'bg-gray-800/80 border-gray-700 text-white placeholder:text-gray-500 focus:border-indigo-500' 
                              : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white'
                        }`}
                     />
                     {internalTestingSearch && (
                        <button 
                           type="button" 
                           onClick={() => setInternalTestingSearch('')} 
                           className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                           <X size={16} />
                        </button>
                     )}
                  </div>

                  {/* Resultados y Usuarios con Excepción */}
                  <div className="space-y-3">
                     {users
                        .filter(u => {
                           const query = internalTestingSearch.trim().toLowerCase();
                           if (!query) {
                              return !!u.internalTestingBypass;
                           }
                           const name = (u.displayName || '').toLowerCase();
                           const email = (u.email || '').toLowerCase();
                           return name.includes(query) || email.includes(query);
                        })
                        .map(u => {
                           const hasBypass = !!u.internalTestingBypass;
                           const isUpdating = updatingTestingBypassUid === u.uid;
                           return (
                              <div 
                                 key={u.uid} 
                                 className={`p-4 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                                    hasBypass
                                       ? isDarkMode
                                          ? 'bg-indigo-950/20 border-indigo-800/60'
                                          : 'bg-indigo-50/50 border-indigo-200'
                                       : isDarkMode
                                          ? 'bg-gray-800/40 border-gray-700/60'
                                          : 'bg-gray-50/70 border-gray-200'
                                 }`}
                              >
                                 <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0 overflow-hidden font-bold text-xs">
                                       {u.photoURL ? (
                                          <img src={u.photoURL} alt="" className="w-full h-full object-cover" />
                                       ) : (
                                          (u.displayName || u.email || 'U').substring(0, 2).toUpperCase()
                                       )}
                                    </div>
                                    <div className="min-w-0">
                                       <div className="flex items-center gap-2 flex-wrap">
                                          <span className={`text-xs font-black truncate uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                             {u.displayName || u.email || u.uid}
                                          </span>
                                          {hasBypass && (
                                             <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-300 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-700">
                                                PRUEBA INTERNA
                                             </span>
                                          )}
                                          {u.role === 'ADMIN' && (
                                             <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300">
                                                ADMIN
                                             </span>
                                          )}
                                       </div>
                                       <div className="flex items-center gap-3 text-[10px] text-gray-400 font-bold uppercase mt-0.5">
                                          <span>{u.email || 'Sin correo registrado'}</span>
                                          <span>•</span>
                                          <span>{u.workCountry || u.country || 'Sin país'}</span>
                                       </div>
                                    </div>
                                 </div>

                                 <div className="flex items-center gap-3 self-end sm:self-center shrink-0">
                                    <button
                                       type="button"
                                       onClick={() => handleToggleInternalTestingBypass(u.uid, hasBypass)}
                                       disabled={isUpdating}
                                       className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-2 border ${
                                          hasBypass
                                             ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-600/20'
                                             : isDarkMode
                                                ? 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700 hover:text-white'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                       }`}
                                    >
                                       {isUpdating ? (
                                          <RefreshCw size={12} className="animate-spin" />
                                       ) : (
                                          <FlaskConical size={12} />
                                       )}
                                       {hasBypass ? 'Desactivar Bypass' : 'Activar Acceso Completo'}
                                    </button>
                                 </div>
                              </div>
                           );
                        })}

                     {internalTestingSearch.trim() === '' && users.filter(u => u.internalTestingBypass).length === 0 && (
                        <div className={`p-8 rounded-2xl border border-dashed text-center ${isDarkMode ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                           <FlaskConical size={24} className="mx-auto mb-2 opacity-40" />
                           <p className="text-[11px] font-black uppercase tracking-wider">No hay colaboradores con bypass de pruebas internas activo</p>
                           <p className="text-[10px] font-bold mt-1">Utiliza el buscador superior para encontrar un usuario y concederle acceso a todas las funciones sin restricciones regionales.</p>
                        </div>
                     )}

                     {internalTestingSearch.trim() !== '' && users.filter(u => {
                        const query = internalTestingSearch.trim().toLowerCase();
                        const name = (u.displayName || '').toLowerCase();
                        const email = (u.email || '').toLowerCase();
                        return name.includes(query) || email.includes(query);
                     }).length === 0 && (
                        <div className={`p-6 rounded-2xl border border-dashed text-center text-[10px] font-black uppercase tracking-wider ${isDarkMode ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                           No se encontraron usuarios que coincidan con "{internalTestingSearch}"
                        </div>
                     )}
                  </div>
               </div>

           </div>
        )}

        {/* BILLING TAB */}
        {activeTab === 'BILLING' && (
          <div className="space-y-6 animate-in fade-in">
            <div className={`p-6 rounded-2xl border ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Cargo Base General (mensual, USD)</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  step="0.01"
                  defaultValue={systemPricing?.baseFeeDefault ?? 30}
                  onBlur={async (e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val)) {
                      await saveSystemPricing({ ...(systemPricing || { models: {}, searchFlatFee: 0.035, lastUpdated: Date.now() }), baseFeeDefault: val });
                    }
                  }}
                  className={`w-40 px-4 py-2 rounded-xl border text-sm font-bold ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-white border-gray-200'}`}
                />
                <span className="text-[10px] font-bold text-gray-400 uppercase">Aplica a todos los usuarios sin cargo individual configurado</span>
              </div>
            </div>

            <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="p-6 pb-0">
                <h3 className="text-sm font-black uppercase tracking-widest text-indigo-500 mb-1">Gastos por Usuario (mes actual)</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Cargo base + consumo real de tokens</p>
              </div>
              {isLoadingBilling ? (
                <div className="p-8 text-center text-[10px] font-bold uppercase text-slate-400">Cargando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className={`text-[9px] font-black uppercase tracking-widest text-slate-400 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <th className="p-4">Usuario</th>
                        <th className="p-4">Cargo Base</th>
                        <th className="p-4">Consumo API</th>
                        <th className="p-4">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingBreakdown.map(b => (
                        <tr key={b.uid} className={`border-b last:border-none ${isDarkMode ? 'border-gray-800' : 'border-gray-50'}`}>
                          <td className="p-4">
                            <div className="text-[11px] font-black">{b.displayName}</div>
                            <div className="text-[9px] text-slate-400">{b.email}</div>
                          </td>
                          <td className="p-4">
                            <input
                              type="number"
                              step="0.01"
                              defaultValue={b.baseFee}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val)) handleUpdateUserBaseFee(b.uid, val);
                              }}
                              className={`w-24 px-2 py-1 rounded-lg border text-[11px] font-bold ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-gray-50 border-gray-200'} ${b.isCustomFee ? 'ring-1 ring-indigo-500' : ''}`}
                              title={b.isCustomFee ? 'Cargo individual configurado' : 'Usando cargo base general'}
                            />
                          </td>
                          <td className="p-4 text-[11px] font-bold">${b.tokenCost.toFixed(2)}</td>
                          <td className="p-4 text-[11px] font-black text-emerald-500">${(b.baseFee + b.tokenCost).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de Mensajería Personal */}
        {messageModal.isOpen && messageModal.user && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`w-full max-w-lg rounded-[40px] border shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-white border-gray-100'}`}>
              <div className="p-8 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                <div>
                  <h3 className={`text-xl font-black tracking-tighter uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Comunicación Directa</h3>
                  <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Enviando a: {messageModal.user.displayName}</p>
                </div>
                <button onClick={() => setMessageModal({ ...messageModal, isOpen: false })} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><X size={24}/></button>
              </div>
              <div className="p-8 space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Mensaje de Validación / Notificación</label>
                  <textarea 
                    value={messageModal.text} 
                    onChange={e => setMessageModal({ ...messageModal, text: e.target.value })}
                    placeholder="Escribe el mensaje para el radiólogo..."
                    className={`w-full h-40 p-5 rounded-3xl border text-xs font-medium outline-none resize-none transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white focus:border-indigo-500' : 'bg-gray-50 border-gray-100 text-gray-900 focus:bg-white focus:border-indigo-400'}`}
                  />
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setMessageModal({ ...messageModal, isOpen: false })}
                    className={`flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleSendMessage}
                    disabled={!messageModal.text.trim()}
                    className="flex-1 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-xl shadow-indigo-600/20 active:scale-95 disabled:opacity-50 disabled:scale-100"
                  >
                    Enviar Mensaje
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmación de Eliminación de Usuario */}
        {deleteConfirmUser && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`w-full max-w-md rounded-[40px] border shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-white border-gray-100'}`}>
              <div className="p-8 text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center mx-auto">
                  <Trash2 size={28} />
                </div>
                <h3 className={`text-xl font-black tracking-tighter uppercase ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  ¿Eliminar a {deleteConfirmUser.displayName || 'este usuario'}?
                </h3>
                <p className={`text-xs font-medium leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Se borra su perfil por completo de esta lista. Esta acción NO se puede deshacer, pero la persona SÍ podrá volver a registrarse más adelante con el mismo correo.
                </p>
                <div className="flex gap-3 pt-2">
                  <button 
                    onClick={() => setDeleteConfirmUser(null)}
                    className={`flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={() => handleDeleteUser(deleteConfirmUser.uid)}
                    className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-xl shadow-red-600/20 active:scale-95"
                  >
                    Eliminar Definitivamente
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Detalle de Perfil Profesional */}
        {viewingProfileUser && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-[40px] border shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-white border-gray-100'}`}>
              
              {/* Encabezado del Modal */}
              <div className="p-6 md:p-8 border-b border-gray-100 dark:border-gray-800 flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden shrink-0">
                    {viewingProfileUser.photoURL ? (
                      <img src={viewingProfileUser.photoURL} className="w-full h-full object-cover" alt="" />
                    ) : (
                      <UserIcon size={32} className="text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h3 className={`text-xl font-black uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {viewingProfileUser.displayName}
                    </h3>
                    <p className="text-xs font-bold text-gray-400">{viewingProfileUser.email}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="px-2.5 py-0.5 bg-indigo-100 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 text-[9px] font-black uppercase rounded-full">
                        {viewingProfileUser.role}
                      </span>
                      {viewingProfileUser.isVerified ? (
                        <span className="flex items-center gap-1 px-2.5 py-0.5 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 text-[9px] font-black uppercase rounded-full">
                          <ShieldCheck size={12} /> Validado Master
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2.5 py-0.5 bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 text-[9px] font-black uppercase rounded-full">
                          <ShieldAlert size={12} /> Pendiente Master
                        </span>
                      )}
                      {viewingProfileUser.internalTestingBypass && (
                        <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-300 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-700">
                          PRUEBA INTERNA
                        </span>
                      )}
                      {viewingProfileUser.isBlocked && (
                        <span className="px-2.5 py-0.5 bg-red-100 text-red-600 text-[9px] font-black uppercase rounded-full">
                          Bloqueado
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => setProfileViewUid(null)} 
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X size={24}/>
                </button>
              </div>

              {/* Cuerpo del Modal */}
              <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar space-y-6">
                
                {/* Datos Profesionales */}
                <div>
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Datos Profesionales e Identificación</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className={`p-3.5 rounded-2xl border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-0.5">DNI / Identificación</span>
                      <span className={`text-xs font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{viewingProfileUser.dni || 'No especificado'}</span>
                    </div>
                    <div className={`p-3.5 rounded-2xl border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-0.5">Nº de Colegiado</span>
                      <span className={`text-xs font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{viewingProfileUser.collegeNumber || 'No especificado'}</span>
                    </div>
                    <div className={`p-3.5 rounded-2xl border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-0.5">Colegio / Institución</span>
                      <span className={`text-xs font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{viewingProfileUser.college || 'No especificado'}</span>
                    </div>
                    <div className={`p-3.5 rounded-2xl border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-0.5">Especialidad Principal</span>
                      <span className="text-xs font-black text-indigo-500">{viewingProfileUser.mainSpecialty || 'No especificada'}</span>
                    </div>
                    <div className={`p-3.5 rounded-2xl border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-0.5">Especialidades Adicionales</span>
                      <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {viewingProfileUser.specialties && viewingProfileUser.specialties.length > 0 ? viewingProfileUser.specialties.join(', ') : 'Ninguna'}
                      </span>
                    </div>
                    <div className={`p-3.5 rounded-2xl border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-0.5">Experiencia / Nivel</span>
                      <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {viewingProfileUser.role === 'RESIDENT' 
                          ? `Residente R${viewingProfileUser.residentYear || 1}` 
                          : (viewingProfileUser.experienceYears ? `${viewingProfileUser.experienceYears} años` : 'No especificada')}
                        {viewingProfileUser.otherMedicalSpecialty ? ` (${viewingProfileUser.otherMedicalSpecialty})` : ''}
                      </span>
                    </div>
                    <div className={`p-3.5 rounded-2xl border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-0.5">Interés Telerradiología</span>
                      <span className={`text-xs font-bold ${viewingProfileUser.teleRadiologyInterest ? 'text-emerald-500' : (isDarkMode ? 'text-gray-400' : 'text-gray-600')}`}>
                        {viewingProfileUser.teleRadiologyInterest ? 'Sí (Disponible)' : 'No'}
                      </span>
                    </div>
                    <div className={`p-3.5 rounded-2xl border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-0.5">Perfil LinkedIn</span>
                      {viewingProfileUser.linkedin ? (
                        <a 
                          href={viewingProfileUser.linkedin.startsWith('http') ? viewingProfileUser.linkedin : `https://${viewingProfileUser.linkedin}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-xs font-bold text-blue-500 hover:underline flex items-center gap-1 truncate"
                        >
                          <Globe size={12} className="shrink-0" />
                          <span className="truncate">{viewingProfileUser.linkedin}</span>
                          <ExternalLink size={10} className="shrink-0" />
                        </a>
                      ) : (
                        <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No especificado</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ubicación y Territorio (Edición Exclusiva Master) */}
                <div className={`p-5 rounded-[28px] border ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-200/80'}`}>
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        <MapPin size={18} />
                      </div>
                      <div>
                        <h4 className={`text-xs font-black uppercase tracking-wide ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Ubicación y Territorio
                        </h4>
                        <p className="text-[9px] font-bold text-gray-400 uppercase">Edición Exclusiva Master (Afecta Control Regional)</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-2.5 py-1 rounded-full border border-amber-200 dark:border-amber-800/50">
                      <Lock size={12} /> Bloqueado a Usuario
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    <div>
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-1">Centro de Trabajo</label>
                      <input 
                        type="text" 
                        value={editWorkplace} 
                        onChange={e => setEditWorkplace(e.target.value)} 
                        placeholder="Ej. Hospital La Paz"
                        className={`w-full px-3 py-2 rounded-xl text-xs font-bold border outline-none transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white focus:border-indigo-500' : 'bg-white border-gray-200 text-gray-900 focus:border-indigo-500'}`}
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-1">Ciudad de Trabajo</label>
                      <input 
                        type="text" 
                        value={editWorkCity} 
                        onChange={e => setEditWorkCity(e.target.value)} 
                        placeholder="Ej. Madrid"
                        className={`w-full px-3 py-2 rounded-xl text-xs font-bold border outline-none transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white focus:border-indigo-500' : 'bg-white border-gray-200 text-gray-900 focus:border-indigo-500'}`}
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider block mb-1">País de Trabajo</label>
                      <select 
                        value={editWorkCountry} 
                        onChange={e => setEditWorkCountry(e.target.value)} 
                        className={`w-full px-3 py-2 rounded-xl text-xs font-bold border outline-none transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white focus:border-indigo-500' : 'bg-white border-gray-200 text-gray-900 focus:border-indigo-500'}`}
                      >
                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  {locationSaveFeedback && (
                    <div className={`p-3 rounded-xl text-xs font-bold mb-3 flex items-center gap-2 ${locationSaveFeedback.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' : 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}`}>
                      {locationSaveFeedback.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                      <span>{locationSaveFeedback.text}</span>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button 
                      onClick={handleSaveLocation} 
                      disabled={isSavingLocation}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md active:scale-95 ${isSavingLocation ? 'bg-gray-600 text-gray-300' : 'bg-amber-600 hover:bg-amber-700 text-white shadow-amber-600/20'}`}
                    >
                      {isSavingLocation ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                      Guardar Ubicación
                    </button>
                  </div>
                </div>

                {/* Sección de Auditoría IA */}
                <div className={`p-5 rounded-[28px] border ${isDarkMode ? 'bg-indigo-950/20 border-indigo-900/40' : 'bg-indigo-50/70 border-indigo-100'}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-xl bg-indigo-600 text-white shadow-md shadow-indigo-600/20">
                        <BrainCircuit size={18} />
                      </div>
                      <div>
                        <h5 className={`text-xs font-black uppercase tracking-wide ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Auditoría IA (Pre-validación Informativa)
                        </h5>
                        <p className="text-[9px] font-bold text-gray-400 uppercase">Grounding de Búsqueda Web & Credenciales</p>
                      </div>
                    </div>

                    <div>
                      {viewingProfileUser.aiPreApproved === true && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 text-[10px] font-black uppercase rounded-full border border-emerald-300 dark:border-emerald-800 shadow-sm">
                          <CheckCircle size={12} /> Pre-aprobado IA {viewingProfileUser.aiConfidenceScore ? `(${viewingProfileUser.aiConfidenceScore}%)` : ''}
                        </span>
                      )}
                      {viewingProfileUser.aiPreApproved === false && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 dark:bg-red-950/60 text-red-700 dark:text-red-300 text-[10px] font-black uppercase rounded-full border border-red-300 dark:border-red-800 shadow-sm">
                          <AlertTriangle size={12} /> Revisión IA Requerida {viewingProfileUser.aiConfidenceScore ? `(${viewingProfileUser.aiConfidenceScore}%)` : ''}
                        </span>
                      )}
                      {viewingProfileUser.aiPreApproved === undefined && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-[10px] font-black uppercase rounded-full border border-gray-300 dark:border-gray-700">
                          Sin auditar
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={`p-4 rounded-2xl border text-xs font-medium leading-relaxed mb-4 whitespace-pre-line ${isDarkMode ? 'bg-[#1e2024] border-gray-800 text-gray-300' : 'bg-white border-gray-200 text-gray-700'}`}>
                    <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-gray-100 dark:border-gray-800">
                      <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest block">Informe del modelo:</span>
                      {viewingProfileUser.lastAuditedAt && (
                        <span className="text-[9px] font-bold text-gray-400">
                          Guardado en BD: {new Date(viewingProfileUser.lastAuditedAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {viewingProfileUser.validationReport || "No hay reporte de auditoría generado aún para este usuario."}
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => handleAiAudit(viewingProfileUser)}
                      disabled={isAuditing === viewingProfileUser.uid}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md active:scale-95 ${
                        isAuditing === viewingProfileUser.uid 
                          ? 'bg-gray-600 text-gray-400' 
                          : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/20'
                      }`}
                    >
                      {isAuditing === viewingProfileUser.uid ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
                      Re-Validar IA
                    </button>
                  </div>
                </div>

                {/* Nota de Control Master */}
                <div className={`p-4 rounded-2xl border flex items-start gap-3 ${viewingProfileUser.isVerified ? (isDarkMode ? 'bg-emerald-950/20 border-emerald-800/40 text-emerald-300' : 'bg-emerald-50 border-emerald-100 text-emerald-800') : (isDarkMode ? 'bg-amber-950/20 border-amber-800/40 text-amber-300' : 'bg-amber-50 border-amber-100 text-amber-800')}`}>
                  <ShieldCheck size={18} className="shrink-0 mt-0.5" />
                  <div className="text-[10px] font-medium leading-relaxed">
                    <p className="font-black uppercase tracking-wide mb-0.5">
                      Validación Manual del Master: {viewingProfileUser.isVerified ? 'VERIFICADO Y AUTORIZADO' : 'PENDIENTE DE AUTORIZACIÓN'}
                    </p>
                    <p className="opacity-80">
                      La validación profesional final es competencia exclusiva del Master mediante los botones de acción del panel. La IA solo genera una pre-aprobación técnica orientativa.
                    </p>
                  </div>
                </div>

              </div>

              {/* Pie del Modal */}
              <div className="p-4 md:p-6 border-t border-gray-100 dark:border-gray-800 flex justify-end bg-gray-50/50 dark:bg-black/20">
                <button
                  onClick={() => setProfileViewUid(null)}
                  className={`px-6 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cerrar
                </button>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// COMPONENTES DE APOYO
const StatCard = ({ title, value, sub, icon: Icon, color, bg, isDarkMode }: any) => (
  <div className={`p-6 rounded-[32px] border shadow-sm flex flex-col transition-all hover:scale-[1.03] ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100 shadow-slate-200/50'}`}>
    <div className={`p-3 w-fit rounded-2xl mb-4 ${bg} ${color} shadow-sm`}><Icon size={20} /></div>
    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">{title}</p>
    <h3 className={`text-3xl font-black tracking-tighter ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</h3>
    <p className="text-[10px] font-bold text-gray-500 mt-1 uppercase">{sub}</p>
  </div>
);

const ModelMetricCard = ({ icon: Icon, label, tokens, color, barColor, isDarkMode, onClick }: any) => (
    <button onClick={onClick} className={`text-left p-6 rounded-3xl border transition-all hover:shadow-lg group ${isDarkMode ? 'bg-[#1e2024] border-gray-800' : 'bg-gray-50 border-gray-100'}`}>
        <div className={`flex items-center gap-2 mb-4 ${color}`}><Icon size={20}/><span className="text-xs font-black uppercase">{label}</span></div>
        <p className={`text-4xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatTokens(tokens)}</p>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Tokens Acumulados</p>
        <div className={`h-1.5 w-full rounded-full overflow-hidden mt-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <div className={`h-full ${barColor} group-hover:scale-x-105 transition-transform origin-left`} style={{ width: '70%' }}></div>
        </div>
        <div className="mt-3 flex justify-end">
            <div className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-indigo-900/40 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>Analizar Histórico</div>
        </div>
    </button>
);

const ComplexityStat = ({ label, count, color, bg, isDarkMode }: any) => (
  <div className={`px-5 py-4 rounded-2xl border flex justify-between items-center transition-colors ${isDarkMode ? 'bg-black/20 border-gray-700 hover:bg-black/40' : 'bg-white border-gray-100 hover:bg-gray-50'}`}>
     <div className="flex items-center gap-3"><div className={`p-2 rounded-xl ${bg} ${color}`}><FileText size={14}/></div><span className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span></div>
     <span className={`text-xs font-black ${color}`}>{count}</span>
  </div>
);

const LogDetail = ({ label, value, isDark }: { label: string, value: string, isDark: boolean }) => (
  <div className={`p-3 rounded-xl border ${isDark ? 'bg-black/20 border-gray-800' : 'bg-gray-50 border-gray-100'}`}>
    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1 block">{label}</span>
    <span className={`text-[10px] font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate block`}>{value}</span>
  </div>
);

const ModelUsageItem = ({ name, tokens, cost, color, isDarkMode }: any) => (
    <div className={`p-4 rounded-2xl border transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
        <div className="flex items-center gap-2 mb-3"><div className={`w-2.5 h-2.5 rounded-full ${color} shadow-sm`}></div><span className={`text-[9px] font-black uppercase tracking-tight ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{name}</span></div>
        <div className="flex justify-between items-end">
            <div><p className="text-[8px] font-bold text-gray-400 uppercase mb-0.5">Consumo</p><p className={`text-sm font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{tokens}</p></div>
            <div className="text-right"><p className="text-[8px] font-bold text-gray-400 uppercase mb-0.5">Facturado</p><p className="text-sm font-black text-indigo-500">{cost}</p></div>
        </div>
    </div>
);

export default ManagementScreen;
